import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import {
  FiArrowRight,
  FiBookmark,
  FiCheck,
  FiChevronRight,
  FiGlobe,
  FiLink,
  FiMapPin,
  FiSearch,
  FiTag,
} from "react-icons/fi";
import {
  BrandLinkedIn,
  BrandX,
  Building,
  Spark,
  Trophy,
} from "../components/ui/icons";
import { createPortal } from "react-dom";
import { AVATAR_TINTS, CHART_TINTS, TIER_RAMP } from "../theme/ThemeContext";
import styled, { css } from "styled-components";
import { useToast } from "../components/ToastProvider";
import { useAuth } from "../context/AuthContext";
import { fetchWithAuth } from "../utils/authFetch";
import CompanyLogo from "../components/CompanyLogo";
import { formatNumber } from "../utils/formatNumber";
import {
  getUserSettings,
  invalidateUserSettingsCache,
  updateCachedUserSettings,
} from "../lib/userSettingsService";
import { useParams, useNavigate } from "react-router-dom";
import Skeleton, { SkeletonText, FadeIn, useFillCount } from "../components/Skeleton";
import { PageScrollLock } from "../utils/useLockPageScroll";


// ─── Design tokens derived from theme ──────────────────────────────
// scoutm8 aesthetic mapped onto the user's light/dark theme palette.
// Every value aliases an app token on :root (see theme/ThemeContext); this page has no palette of its own.
const TOKENS = {
  bg: "var(--bg)",
  bg2: "var(--bg-2)",
  surface: "var(--surface)",
  surfaceTint: "var(--surface-tint)",
  ink: "var(--ink)",
  inkSoft: "var(--ink-soft)",
  inkMuted: "var(--ink-muted)",
  inkFaint: "var(--ink-faint)",
  line: "var(--line)",
  lineSoft: "var(--line-soft)",
  lineStrong: "var(--line-strong)",
  accent: "var(--accent)",
  accentInk: "var(--accent-ink)",
  accentSoft: "var(--accent-soft)",
  success: "var(--success)",
  successSoft: "var(--success-soft)",
  info: "var(--info)",
  infoSoft: "var(--info-soft)",
  warn: "var(--warn)",
  warnSoft: "var(--warn-soft)",
  fontDisplay: "var(--font-display)",
  fontBody: "var(--font-body)",
  fontMono: "var(--font-mono)",
  shadowSm: "var(--shadow-sm)",
  shadowMd: "var(--shadow-md)",
};

const Container = styled.div`
  max-width: 1320px;
  width: calc(100% - 48px);
  margin: 0 auto;
  min-height: 100vh;
  font-family: ${TOKENS.fontBody};
  color: ${TOKENS.ink};

  /* 24px per side on top of MainContent's padding wastes a phone's width. */
  @media (max-width: 450px) {
    width: calc(100% - 8px);
  }
  @media (max-width: 390px) {
    width: calc(100% - 16px);
    padding: 0 8px;
  }
`;

// ─── Hero ─────────────────────────────────────────────────────────
const Hero = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 36px;
  align-items: end;
  padding: 28px 0 26px;
  border-bottom: 1px solid ${TOKENS.line};
  margin-bottom: 22px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 22px;
  }
`;

const HeroEyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${TOKENS.accentSoft};
  color: ${TOKENS.accentInk};
  padding: 5px 12px;
  border-radius: 99px;
  font-size: 11.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 14px;
`;

const HeroTitle = styled.h1`
  font-family: ${TOKENS.fontDisplay};
  font-size: 60px;
  font-weight: 400;
  line-height: 1;
  margin: 0;
  color: ${TOKENS.ink};
  letter-spacing: -0.015em;

  @media (max-width: 768px) {
    font-size: 44px;
  }
  @media (max-width: 390px) {
    font-size: 36px;
  }
`;

const HeroSub = styled.p`
  font-size: 16px;
  color: ${TOKENS.inkSoft};
  line-height: 1.5;
  margin: 12px 0 0;
  max-width: 560px;
`;

const HeroStats = styled.div`
  display: flex;
  gap: 26px;
  flex-shrink: 0;
  flex-wrap: wrap;
`;

const HeroStat = styled.div`
  text-align: left;
`;

const HeroStatNum = styled.div`
  font-family: ${TOKENS.fontDisplay};
  font-size: 30px;
  font-weight: 400;
  line-height: 1;
  color: ${({ $accent }) =>
    $accent ? TOKENS.accent : TOKENS.ink};
  letter-spacing: -0.005em;
`;

const HeroStatLbl = styled.div`
  font-size: 11.5px;
  color: ${TOKENS.inkMuted};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 600;
  margin-top: 4px;
  white-space: nowrap;
`;

// ─── Search row ───────────────────────────────────────────────────
const SearchRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 14px;
  position: relative;
  /* Phone: the field takes a row, the filters take the next one. Unwrapped,
     the chips sit beside a field that has already shrunk to nothing and still
     overflow the right edge, because ChipFilters refuses to shrink. */
  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 10px;
  }
`;

const SearchForm = styled.form`
  position: relative;
  width: 100%;
  margin-bottom: 0;
  flex: 1;
  @media (max-width: 768px) {
    flex: 1 1 100%;
  }
`;

const Input = styled.input`
  width: 100%;
  height: 44px;
  padding: 0 140px 0 42px;
  border-radius: 10px;
  border: 1px solid ${TOKENS.line};
  font-size: 14px;
  outline: none;
  background: ${TOKENS.surfaceTint};
  color: ${TOKENS.ink};
  transition: border-color 0.12s, background 0.12s, box-shadow 0.12s;

  &::placeholder {
    color: ${TOKENS.inkMuted};
  }

  &:focus {
    background: ${TOKENS.surface};
    border-color: ${TOKENS.accent};
    box-shadow: 0 0 0 3px ${TOKENS.accentSoft};
  }

  @media (max-width: 390px) {
    padding: 0 110px 0 36px;
    font-size: 13px;
    height: 42px;
  }
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: ${TOKENS.inkMuted};
  display: flex;
  align-items: center;
  pointer-events: none;
  z-index: 1;
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
`;

const SuggestionList = styled.ul`
  position: absolute;
  margin: 0;
  padding: 6px;
  list-style: none;
  background: ${TOKENS.surface};
  border: 1px solid ${TOKENS.line};
  width: 100%;
  max-height: 240px;
  overflow: auto;
  border-radius: 10px;
  box-shadow: ${TOKENS.shadowMd};
  top: calc(100% + 6px);
  z-index: 30;
`;

const SuggestionItem = styled.li`
  padding: 8px 10px;
  cursor: pointer;
  color: ${TOKENS.ink};
  border-radius: 6px;
  font-size: 13px;
  &:hover {
    background: ${TOKENS.bg2};
  }
`;

const Button = styled.button`
  background: ${TOKENS.ink};
  color: var(--on-fill);
  border: 1px solid ${TOKENS.ink};
  border-radius: 8px;
  padding: 0 14px;
  height: 36px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, transform 0.06s;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: ${TOKENS.accent};
    border-color: ${TOKENS.accent};
  }
  &:active {
    transform: translateY(0.5px);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  /* Global 44px touch rule bloats the 30–36px buttons in card heads and rows. */
  @media (max-width: 450px) {
    min-height: 36px;
  }
`;

const ButtonGhost = styled(Button)`
  background: transparent;
  color: ${TOKENS.ink};
  border: 1px solid ${TOKENS.line};

  &:hover {
    background: ${TOKENS.bg2};
    border-color: ${TOKENS.lineStrong};
    color: ${TOKENS.ink};
  }
`;

const ButtonAccent = styled(Button)`
  background: ${TOKENS.accent};
  border-color: ${TOKENS.accent};
  color: var(--on-fill);
  &:hover {
    background: ${TOKENS.ink};
    border-color: ${TOKENS.ink};
  }
`;

const SearchButton = styled(ButtonAccent)`
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  margin: 0;
  z-index: 2;
  height: 32px;
  padding: 0 12px;
  font-size: 12.5px;

  /* Sits inside the 42–44px input; the touch-target floor would make it as tall as the field. */
  @media (max-width: 450px) {
    min-height: 0;
  }
  @media (max-width: 390px) {
    right: 3px;
    height: 32px;
    padding: 0 10px;
  }
`;

const ChipFilters = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;
  flex-wrap: wrap;
  /* Full row of its own, and shrinkable — flex-shrink: 0 above is what pins it
     at max-content and stops its own flex-wrap from ever taking effect. */
  @media (max-width: 768px) {
    flex: 1 1 100%;
    flex-shrink: 1;
  }
`;

// Stationary outer frame. Holds bg/border/shadow. Doesn't scroll, so macOS
// rubber-band can never displace it or expose what's behind.
const Menu = styled.div`
  position: absolute;
  background-color: ${"var(--surface)"};
  background-clip: padding-box;
  border: 1px solid ${TOKENS.line};
  border-radius: 10px;
  box-shadow: ${TOKENS.shadowMd};
  min-width: 200px;
  max-width: 320px;
  z-index: 1000;
  overflow: hidden;
  isolation: isolate;
`;

// Anchored locations popover — same shape as the Jobs page's location
// dropdown (outer clip + isolated inner scroll, overscroll contained).
const RegionsPopover = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 60;
  background: ${TOKENS.surface};
  border: 1px solid ${TOKENS.line};
  border-radius: 10px;
  box-shadow: ${TOKENS.shadowMd};
  min-width: 240px;
  max-width: 320px;
  cursor: default;
  overflow: hidden;
  isolation: isolate;
`;
const RegionsPopoverScroll = styled.div`
  padding: 10px 12px 12px;
  max-height: 320px;
  overflow-y: auto;
  overscroll-behavior: contain;
  .lp-head {
    font-size: 10.5px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: ${TOKENS.inkMuted};
    font-weight: 600;
    padding: 0 2px 8px;
  }
  .lp-row {
    padding: 5px 2px;
    font-size: 12.5px;
    color: ${TOKENS.inkSoft};
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

// Inner scroll container. Bounce stays inside the frame visually.
const MenuScroll = styled.div`
  padding: 6px;
  overflow-y: auto;
  overscroll-behavior: contain;
`;

const MenuItem = styled.div`
  position: relative;
  padding: 7px 28px 7px 10px;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.4;
  cursor: pointer;
  color: ${TOKENS.ink};
  word-break: break-word;
  overflow-wrap: anywhere;

  &:hover {
    background: ${TOKENS.bg2};
  }

  /* The trailing check icon (when an option is selected) sits at the
     right edge regardless of how many lines the label wraps to. */
  & > svg {
    position: absolute;
    right: 10px;
    top: 9px;
    margin: 0 !important;
  }
`;

const MenuDivider = styled.div`
  height: 1px;
  background: ${TOKENS.lineSoft};
  margin: 4px 0;
`;

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 12px;
  border-radius: 99px;
  background: ${({ $active }) =>
    $active ? TOKENS.ink : TOKENS.surface};
  border: 1px solid
    ${({ $active }) =>
      $active ? TOKENS.ink : TOKENS.line};
  color: ${({ $active }) =>
    $active ? "var(--on-fill)" : TOKENS.inkSoft};
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
  white-space: nowrap;

  &:hover {
    background: ${({ $active }) =>
      $active ? TOKENS.ink : TOKENS.surfaceTint};
    border-color: ${TOKENS.lineStrong};
    color: ${({ $active }) =>
      $active ? "var(--on-fill)" : TOKENS.ink};
  }
  /* Last, so these beat the base height/padding/font-size above at equal
     specificity. Four chips on two rows should not dominate the page. */
  @media (max-width: 560px) {
    height: 32px;
    /* Global 44px touch-target floor in App.js would clamp the height. */
    min-height: 0;
    padding: 0 10px;
    font-size: 12px;
    gap: 5px;
  }
  @media (max-width: 390px) {
    padding: 0 8px;
    font-size: 11.5px;
  }
`;

const IconButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${TOKENS.surface};
  border: 1px solid ${TOKENS.line};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${TOKENS.inkSoft};
  cursor: pointer;
  padding: 0;
  font-size: 14px;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
  box-sizing: border-box;
  flex-shrink: 0;
  overflow: hidden;
  position: relative;

  &:hover {
    background: ${TOKENS.bg2};
    color: ${TOKENS.ink};
    border-color: ${TOKENS.lineStrong};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  img {
    flex-shrink: 0;
    display: block;
    max-width: 100%;
    max-height: 100%;
  }
`;

const IconButtonWrapper = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Tooltip = styled.span`
  position: absolute;
  bottom: 44px;
  left: 50%;
  transform: translateX(-50%);
  background: ${TOKENS.ink};
  color: var(--on-fill);
  padding: 5px 9px;
  font-size: 12px;
  border-radius: 6px;
  white-space: nowrap;
  box-shadow: ${TOKENS.shadowMd};
  z-index: 40;
  pointer-events: none;
  opacity: 0;

  ${IconButtonWrapper}:hover &,
  ${IconButtonWrapper}:focus-within & {
    opacity: 1;
  }
`;

const CountLine = styled.div`
  font-size: 13px;
  color: ${TOKENS.inkMuted};
  margin-bottom: 12px;
  b {
    color: ${TOKENS.ink};
    font-weight: 600;
  }
`;

const CardsContainer = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 0;
  margin: 0 auto 1.5rem auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CompanyCard = styled.div`
  display: grid;
  grid-template-columns: 56px minmax(0, 2.6fr) auto auto;
  align-items: center;
  gap: 18px;
  padding: 16px 20px;
  background: ${TOKENS.surface};
  border: 1px solid ${TOKENS.line};
  border-radius: 14px;
  cursor: pointer;
  transition: border-color 0.12s, box-shadow 0.12s;
  word-break: break-word;

  &:hover {
    border-color: ${TOKENS.lineStrong};
    box-shadow: ${TOKENS.shadowMd};
  }

  @media (max-width: 768px) {
    grid-template-columns: 56px 1fr auto;
  }
  @media (max-width: 768px) {
    & > *:nth-child(3) {
      display: none;
    }
  }
  /* Below 450px the Research button column leaves the name ~100px; move it to its own row. */
  @media (max-width: 450px) {
    grid-template-columns: 48px 1fr;
    gap: 12px;
    padding: 12px 14px;
    & > *:nth-child(4) {
      grid-column: 1 / -1;
      justify-self: stretch;
    }
  }
`;

const CompanyInfo = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CompanyTitle = styled.div`
  font-family: ${TOKENS.fontDisplay};
  font-size: 22px;
  font-weight: 400;
  line-height: 1.15;
  color: ${TOKENS.ink};
  letter-spacing: -0.005em;
`;

const CompanyDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 3px 14px;
  font-size: 12.5px;
  color: ${TOKENS.inkSoft};
  margin-top: 2px;
`;

const CompanyDetailItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12.5px;
  color: ${TOKENS.inkSoft};
`;

const PillTag = styled.span`
  display: inline-flex;
  align-items: center;
  /* min-height, not height: a long label wraps to a second line rather than
     running past the edge. nowrap plus a fixed height is what let values like
     "Intellectual property: PRC firms: contentious" overflow the screen. */
  min-height: 22px;
  max-width: 100%;
  padding: 2px 8px;
  border-radius: 6px;
  background: ${TOKENS.bg2};
  color: ${TOKENS.inkSoft};
  font-size: 11.5px;
  font-weight: 500;
  line-height: 1.35;
  overflow-wrap: anywhere;
  /* Long taxonomy labels earn their space on desktop but crowd a phone row,
     where the industry is already reachable through the Industry filter. */
  ${({ $hideOnMobile }) =>
    $hideOnMobile &&
    css`
      @media (max-width: 560px) {
        display: none;
      }
    `}
`;

const CompanyRowStats = styled.div`
  display: flex;
  gap: 24px;
  @media (max-width: 900px) {
    display: none;
  }
`;

const CompanyRowStat = styled.div`
  text-align: right;
`;
const CompanyRowStatNum = styled.div`
  font-family: ${TOKENS.fontDisplay};
  font-size: 22px;
  font-weight: 400;
  line-height: 1;
  color: ${({ $accent }) =>
    $accent ? TOKENS.accent : TOKENS.ink};
`;
const CompanyRowStatLbl = styled.div`
  font-size: 10.5px;
  color: ${TOKENS.inkMuted};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 600;
  margin-top: 3px;
  white-space: nowrap;
`;

const ResearchButton = styled(ButtonAccent)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const CompanyActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

// ─── Firm header (detail page) ────────────────────────────────────
const FirmHead = styled.div`
  background: ${TOKENS.surface};
  border: 1px solid ${TOKENS.line};
  border-radius: 16px;
  padding: 24px 26px;
  display: grid;
  grid-template-columns: 72px 1fr auto;
  gap: 22px;
  align-items: flex-start;
  margin-bottom: 14px;

  @media (max-width: 900px) {
    grid-template-columns: 64px 1fr;
    & > *:nth-child(3) {
      grid-column: 1 / -1;
    }
  }
  /* The 72px logo (inline size) overflows its column; shrink it and the padding for phones. */
  @media (max-width: 450px) {
    grid-template-columns: 52px minmax(0, 1fr);
    gap: 14px;
    padding: 18px 16px;
    & > *:first-child {
      width: 52px !important;
      height: 52px !important;
      font-size: 22px !important;
    }
  }
`;


const FirmHeadName = styled.h1`
  font-family: ${TOKENS.fontDisplay};
  font-size: 36px;
  font-weight: 400;
  line-height: 1.1;
  color: ${TOKENS.ink};
  letter-spacing: -0.01em;
  margin: 0;
  @media (max-width: 450px) {
    font-size: 28px;
  }
`;

const FirmHeadMeta = styled.div`
  margin-top: 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 3px 16px;
  font-size: 12.5px;
  color: ${TOKENS.inkMuted};
`;

const FirmHeadDesc = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: ${TOKENS.inkSoft};
  margin: 12px 0 14px;
  max-width: 760px;
`;

const FirmHeadRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
`;

const FirmHeadLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12.5px;
  color: ${TOKENS.inkSoft};
  text-decoration: none;
  padding: 4px 10px;
  border-radius: 99px;
  transition: background 0.12s, color 0.12s;
  &:hover {
    background: ${TOKENS.bg2};
    color: ${TOKENS.ink};
  }
`;

const FirmHeadActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

// ─── Stat strip ───────────────────────────────────────────────────
const FirmStats = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  background: ${TOKENS.surface};
  border: 1px solid ${TOKENS.line};
  border-radius: 16px;
  padding: 18px 24px;
  margin-bottom: 14px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
  @media (max-width: 450px) {
    padding: 16px;
  }
`;

const FirmStat = styled.div`
  padding: 0 16px;
  border-right: 1px solid ${TOKENS.lineSoft};

  &:first-child {
    padding-left: 0;
  }
  &:last-child {
    border-right: 0;
    padding-right: 0;
  }

  @media (max-width: 900px) {
    &:nth-child(2n) {
      border-right: 0;
    }
    /* Second-row first stat kept its 16px left inset and sat out of line with the first. */
    &:nth-child(2n + 1) {
      padding-left: 0;
    }
  }
`;

const FirmStatNum = styled.div`
  font-family: ${TOKENS.fontDisplay};
  font-size: 38px;
  font-weight: 400;
  line-height: 1;
  letter-spacing: -0.01em;
  color: ${({ $accent }) =>
    $accent ? TOKENS.accent : TOKENS.ink};
  @media (max-width: 450px) {
    font-size: 32px;
  }
`;

const FirmStatLbl = styled.div`
  font-size: 12px;
  color: ${TOKENS.inkMuted};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 600;
  margin-top: 6px;
`;

const FirmStatSub = styled.div`
  font-size: 12px;
  color: ${TOKENS.inkMuted};
  margin-top: 3px;
`;

// ─── Bento grid ──────────────────────────────────────────────────
// `container-type: inline-size` lets children respond to the grid's own width
// (not the viewport), so when the sidebar expands and the available width
// drops, columns collapse cleanly rather than overlapping.
const FirmGrid = styled.div`
  container-type: inline-size;
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 14px;

  @container (max-width: 980px) {
    & > * {
      grid-column: span 12 !important;
    }
  }
  /* Safari < 16 / very old browsers: fall back to viewport breakpoint */
  @media (max-width: 1100px) {
    & > * {
      grid-column: span 12 !important;
    }
  }
`;

const FirmCard = styled.div`
  container-type: inline-size;
  background: ${TOKENS.surface};
  border: 1px solid ${TOKENS.line};
  border-radius: 14px;
  padding: 18px 20px;
  grid-column: span ${({ $span }) => $span || 12};
  min-width: 0;
  @media (max-width: 450px) {
    padding: 16px 14px;
  }
`;

const FirmCardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
`;

const FirmCardH = styled.div`
  font-family: ${TOKENS.fontDisplay};
  font-size: 22px;
  font-weight: 400;
  line-height: 1.15;
  color: ${TOKENS.ink};
  letter-spacing: -0.005em;
`;

const FirmCardSub = styled.div`
  font-size: 12.5px;
  color: ${TOKENS.inkMuted};
  margin-top: 2px;
`;

// ─── Trend ────────────────────────────────────────────────────────
const Seg = styled.div`
  display: inline-flex;
  background: ${TOKENS.bg2};
  border-radius: 99px;
  padding: 3px;
`;

const SegBtn = styled.button`
  height: 28px;
  padding: 0 14px;
  border: 0;
  background: ${({ $active }) =>
    $active ? TOKENS.ink : "transparent"};
  color: ${({ $active }) =>
    $active ? "var(--on-fill)" : TOKENS.inkSoft};
  border-radius: 99px;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
  &:hover {
    color: ${({ $active }) =>
      $active ? "var(--on-fill)" : TOKENS.ink};
  }
  /* Keep the segmented pill compact instead of 44px tall. */
  @media (max-width: 450px) {
    min-height: 0;
    height: 30px;
    padding: 0 11px;
  }
`;

const TrendRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
`;

const TrendBullets = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid ${TOKENS.lineSoft};

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const TrendBullet = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 8px;
  border-right: 1px solid ${TOKENS.lineSoft};
  &:last-child {
    border-right: 0;
  }
  @media (max-width: 600px) {
    &:nth-child(2n) {
      border-right: 0;
    }
  }
`;

const TrendBulletNum = styled.span`
  font-family: ${TOKENS.fontDisplay};
  font-size: 24px;
  font-weight: 400;
  line-height: 1;
  color: ${TOKENS.ink};
`;

const TrendBulletLbl = styled.span`
  font-size: 11.5px;
  color: ${TOKENS.inkMuted};
  margin-top: 3px;
`;

const TrendChartWrap = styled.div`
  width: 100%;
  height: 160px;
  min-width: 0;
  overflow: hidden;
`;

// ─── Country list ────────────────────────────────────────────────
const CountryBody = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 18px;
  align-items: center;

  /* Stack inside the firm-card when the card itself becomes too narrow
     to fit the donut + list side by side (e.g. sidebar expanded). */
  @container (max-width: 360px) {
    grid-template-columns: 1fr;
    justify-items: center;
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    justify-items: center;
  }
`;

const CountryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
`;

const CountryRow = styled.div`
  display: grid;
  grid-template-columns: 8px 1fr auto;
  gap: 10px;
  align-items: center;
  font-size: 12.5px;
  color: ${TOKENS.ink};
  padding: 3px 0;
`;

const CountryDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: ${({ $color }) => $color};
`;

const CountryName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${TOKENS.inkSoft};
`;

const CountryNum = styled.span`
  font-weight: 600;
  color: ${TOKENS.ink};
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

// ─── Exec / contact lists ────────────────────────────────────────
const ExecList = styled.div`
  display: flex;
  flex-direction: column;
`;

const ExecRow = styled.div`
  display: grid;
  grid-template-columns: 36px 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid ${TOKENS.lineSoft};
  &:last-child {
    border-bottom: 0;
  }
`;

const ExecAvatar = styled.span`
  width: 36px;
  height: 36px;
  border-radius: 99px;
  background: ${({ $tintBg }) => $tintBg || TOKENS.bg2};
  color: ${({ $tintFg }) => $tintFg || TOKENS.ink};
  font-size: 12.5px;
  font-weight: 600;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  letter-spacing: 0.02em;
`;

const ExecName = styled.div`
  font-size: 13.5px;
  font-weight: 600;
  color: ${TOKENS.ink};
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ExecTitle = styled.div`
  font-size: 12px;
  color: ${TOKENS.inkMuted};
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// ─── Jobs list (in bento card) ───────────────────────────────────
const JobList = styled.div`
  display: flex;
  flex-direction: column;
`;

const JobRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid ${TOKENS.lineSoft};
  cursor: pointer;
  &:last-child {
    border-bottom: 0;
  }
  &:hover .job-row-title {
    color: ${TOKENS.accent};
  }
`;

const JobRowTitle = styled.div`
  font-size: 13.5px;
  font-weight: 600;
  color: ${TOKENS.ink};
  line-height: 1.25;
  transition: color 0.12s;
`;

const JobRowMeta = styled.div`
  margin-top: 3px;
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  font-size: 11.5px;
  color: ${TOKENS.inkMuted};
  align-items: center;
  .sep {
    color: ${TOKENS.inkFaint};
    margin: 0 4px;
  }
`;

// ─── Signals / news ─────────────────────────────────────────────
const SignalList = styled.div`
  display: flex;
  flex-direction: column;
`;

const SignalRow = styled.div`
  display: grid;
  grid-template-columns: 32px 1fr;
  gap: 12px;
  padding: 11px 0;
  border-bottom: 1px solid ${TOKENS.lineSoft};
  align-items: flex-start;
  &:last-child {
    border-bottom: 0;
  }
`;

const SignalIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 9px;
  display: grid;
  place-items: center;
  background: ${({ $bg }) => $bg};
  color: ${({ $fg }) => $fg};
`;

const SignalText = styled.div`
  font-size: 13.5px;
  color: ${TOKENS.ink};
  line-height: 1.4;
`;

const SignalWhen = styled.div`
  font-size: 11.5px;
  color: ${TOKENS.inkMuted};
  margin-top: 3px;
`;

const AiPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: ${TOKENS.accentSoft};
  color: ${TOKENS.accentInk};
  height: 22px;
  padding: 0 8px;
  border-radius: 99px;
  font-size: 11.5px;
  font-weight: 500;
`;

// Action buttons row (Legal500 / Compose actions)
const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 12px;
`;

// ─── Pulse dot ──────────────────────────────────────────────────
const PulseDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 99px;
  background: ${TOKENS.accent};
  vertical-align: middle;
  margin-left: 8px;
  animation: pulseDot 1.4s ease-in-out infinite;

  @keyframes pulseDot {
    0% {
      box-shadow: 0 0 0 0 ${TOKENS.accentSoft};
    }
    70% {
      box-shadow: 0 0 0 8px transparent;
    }
    100% {
      box-shadow: 0 0 0 0 transparent;
    }
  }
`;


const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  border-radius: 7px;
  background: transparent;
  color: ${TOKENS.inkSoft};
  border: 1px solid transparent;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
  &:hover {
    background: ${TOKENS.bg2};
    color: ${TOKENS.ink};
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--scrim);
  backdrop-filter: blur(2px);
  z-index: 2600;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;

  @media (max-width: 390px) {
    padding: 0.5rem;
    align-items: flex-start;
    padding-top: 1rem;
  }
`;

const ModalContent = styled.div`
  background: ${TOKENS.surface};
  border: 1px solid ${TOKENS.line};
  border-radius: 16px;
  max-width: 700px;
  width: 95vw;
  max-height: 80vh;
  overflow-y: auto;
  padding: 24px;
  box-shadow: var(--shadow-lg);
  position: relative;
  color: ${TOKENS.ink};

  @media (max-width: 768px) {
    padding: 18px;
    border-radius: 14px;
    max-width: 90vw;
  }

  @media (max-width: 390px) {
    padding: 14px;
    border-radius: 12px;
    max-width: 95vw;
    max-height: 85vh;
  }
`;

const ModalClose = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: transparent;
  border: 0;
  font-size: 22px;
  color: ${TOKENS.inkSoft};
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${TOKENS.bg2};
    color: ${TOKENS.ink};
  }
`;

// Inline SVG line chart with hover tooltip. Renders into a fixed 600×height
// viewBox and stretches via `width="100%"` + preserveAspectRatio="none", so it
// always fits its container — no measurement loop, no min-content deadlock.
function LineChartSVG({ data, height = 160, color, tooltipColor }) {
  const wrapRef = useRef(null);
  const [hoverIdx, setHoverIdx] = useState(-1);
  const [wrapW, setWrapW] = useState(0);

  // Track the wrapper's pixel width only for tooltip positioning. Layout never
  // depends on this — the SVG itself is fluid via viewBox.
  useLayoutEffect(() => {
    if (!wrapRef.current) return;
    const measure = () => {
      const w = wrapRef.current?.getBoundingClientRect().width || 0;
      setWrapW(w);
    };
    measure();
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(wrapRef.current);
    } else {
      window.addEventListener("resize", measure);
    }
    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", measure);
    };
  }, []);

  const gradId = React.useMemo(
    () => "lineFill_" + Math.random().toString(36).slice(2, 8),
    [],
  );

  if (!data || data.length === 0) {
    return <div ref={wrapRef} style={{ width: "100%", height, minWidth: 0 }} />;
  }

  const points = data.map((d, i) =>
    typeof d === "number"
      ? { label: String(i), value: d }
      : {
          label: d.label ?? d.date ?? String(i),
          value: Number(d.value ?? d.count ?? 0),
        },
  );

  // Logical viewBox — independent of container size.
  const VBW = 600;
  const VBH = height;
  const pad = { t: 10, r: 10, b: 10, l: 10 };
  const innerW = VBW - pad.l - pad.r;
  const innerH = VBH - pad.t - pad.b;
  const max = Math.max(...points.map((p) => p.value), 1);
  const step = innerW / (points.length - 1 || 1);

  const coords = points.map((p, i) => [
    pad.l + i * step,
    pad.t + innerH - (p.value / max) * innerH,
  ]);

  const pathD = coords
    .map((c, i) => (i === 0 ? "M" : "L") + c[0] + "," + c[1])
    .join(" ");
  const areaD =
    pathD +
    ` L ${pad.l + innerW},${pad.t + innerH} L ${pad.l},${pad.t + innerH} Z`;

  // Cursor → data index: ratio of mouse-X within wrapper width.
  const handleMove = (e) => {
    if (!wrapRef.current || points.length === 0) return;
    const rect = wrapRef.current.getBoundingClientRect();
    if (rect.width <= 0) return;
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    if (points.length === 1) {
      setHoverIdx(0);
      return;
    }
    // padX in vb = pad.l → ratio of pad.l/VBW; account for left/right padding.
    const leftR = pad.l / VBW;
    const rightR = (VBW - pad.r) / VBW;
    const inner = Math.max(0, Math.min(1, (ratio - leftR) / (rightR - leftR)));
    const i = Math.round(inner * (points.length - 1));
    setHoverIdx(Math.max(0, Math.min(points.length - 1, i)));
  };
  const handleLeave = () => setHoverIdx(-1);

  const active = hoverIdx >= 0 ? coords[hoverIdx] : null;
  const activePoint = hoverIdx >= 0 ? points[hoverIdx] : null;

  // Convert vb coords → pixel coords for the tooltip overlay.
  const xRatio = active ? active[0] / VBW : 0;
  const yRatio = active ? active[1] / VBH : 0;
  const tipPxLeft = xRatio * wrapW;
  const tipPxTop = yRatio * height;
  const tooltipW = 140;
  let tipLeft = tipPxLeft - tooltipW / 2;
  if (tipLeft < 4) tipLeft = 4;
  if (tipLeft + tooltipW > wrapW - 4) tipLeft = wrapW - tooltipW - 4;
  const tipTop = Math.max(0, tipPxTop - 58);

  return (
    <div
      ref={wrapRef}
      style={{
        position: "relative",
        width: "100%",
        height,
        minWidth: 0,
      }}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
    >
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 ${VBW} ${VBH}`}
        preserveAspectRatio="none"
        style={{ display: "block" }}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.22" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0, 0.25, 0.5, 0.75, 1].map((f, i) => (
          <line
            key={i}
            x1={pad.l}
            x2={pad.l + innerW}
            y1={pad.t + innerH * f}
            y2={pad.t + innerH * f}
            stroke="currentColor"
            opacity="0.12"
            strokeWidth="0.6"
            strokeDasharray="2 3"
            vectorEffect="non-scaling-stroke"
          />
        ))}
        <path d={areaD} fill={`url(#${gradId})`} />
        <path
          d={pathD}
          fill="none"
          stroke={color}
          strokeWidth="1.8"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
        {coords.map((c, i) =>
          points[i].value > 0 ? (
            <circle
              key={i}
              cx={c[0]}
              cy={c[1]}
              r="3"
              fill={color}
              vectorEffect="non-scaling-stroke"
            />
          ) : null,
        )}
        {active && (
          <>
            <line
              x1={active[0]}
              x2={active[0]}
              y1={pad.t}
              y2={pad.t + innerH}
              stroke={color}
              strokeWidth="1"
              strokeDasharray="2 3"
              opacity="0.6"
              vectorEffect="non-scaling-stroke"
            />
            <circle
              cx={active[0]}
              cy={active[1]}
              r="6"
              fill={color}
              opacity="0.2"
              vectorEffect="non-scaling-stroke"
            />
            <circle
              cx={active[0]}
              cy={active[1]}
              r="4"
              fill={color}
              stroke="var(--on-fill)"
              strokeWidth="1.5"
              vectorEffect="non-scaling-stroke"
            />
          </>
        )}
      </svg>
      {active && activePoint && wrapW > 0 && (
        <div
          style={{
            position: "absolute",
            left: tipLeft,
            top: tipTop,
            width: tooltipW,
            pointerEvents: "none",
            background: tooltipColor?.bg || "var(--ink)",
            color: tooltipColor?.fg || "var(--on-fill)",
            borderRadius: 8,
            padding: "8px 10px",
            fontSize: 12,
            lineHeight: 1.3,
            boxShadow: "var(--shadow-md)",
            textAlign: "center",
            zIndex: 5,
          }}
        >
          <div style={{ opacity: 0.75, fontSize: 11, marginBottom: 2 }}>
            {activePoint.label}
          </div>
          <div
            style={{
              fontWeight: 600,
              fontSize: 14,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatNumber(activePoint.value)}{" "}
            <span style={{ opacity: 0.7, fontWeight: 400, fontSize: 11 }}>
              {activePoint.value === 1 ? "role" : "roles"}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

/* Signal counts cover a rolling 30 days rather than the calendar month: on the
   1st the month-to-date figure is near zero for almost every firm, which reads
   as "no coverage" instead of "early in the month". Matches the hiring-trends
   card, which already labels its rolling 30-day figure "this month". */
const SIGNAL_WINDOW_DAYS = 30;
// Custom (tracked) companies are enriched by a background worker after the first
// scrape; the page polls while any stage is still pending.
const ENRICH_POLL_MS = 5000;
const ENRICH_POLL_MAX_MS = 10 * 60 * 1000;
const isEnriching = (r) =>
  r?.source === "custom" &&
  r?.enrichment &&
  !["done", "failed"].includes(r.enrichment.status);

const DONUT_PALETTE = CHART_TINTS;

/* `total` is the real role count. The ring's own segments cannot supply it: a
   posting listed in three countries contributes to three slices, and postings
   with no standardized country are absent entirely — so summing the slices
   overstates the first case and understates the second. Segments still size
   themselves against their own sum, which is what makes the proportions right. */
function DonutChartSVG({
  data,
  total: totalProp,
  size = 180,
  thickness = 26,
  inkColor,
  mutedColor,
}) {
  if (!data || data.length === 0) return null;
  const segmentSum = data.reduce((a, b) => a + (b.count || 0), 0);
  if (segmentSum === 0) return null;
  const total = totalProp ?? segmentSum;
  const r = size / 2 - thickness / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      style={{ width: size, height: size }}
    >
      {data.map((d, i) => {
        const frac = (d.count || 0) / segmentSum;
        const dash = frac * circ;
        const seg = (
          <circle
            key={(d.country || d.category || "") + i}
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={DONUT_PALETTE[i % DONUT_PALETTE.length]}
            strokeWidth={thickness}
            strokeDasharray={`${dash} ${circ - dash}`}
            strokeDashoffset={-offset}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        );
        offset += dash;
        return seg;
      })}
      <text
        x={cx}
        y={cy - 4}
        textAnchor="middle"
        fontFamily='"Instrument Serif", Georgia, serif'
        fontSize="28"
        fill={inkColor}
      >
        {formatNumber(total)}
      </text>
      <text
        x={cx}
        y={cy + 14}
        textAnchor="middle"
        fontSize="10"
        fontWeight="600"
        letterSpacing=".06em"
        fill={mutedColor}
      >
        ROLES
      </text>
    </svg>
  );
}

// Portalled dropdown anchored to a trigger via ref. Position is `fixed` and
// recomputed on scroll/resize, so it escapes ancestor `overflow` clipping and
// stays attached to the trigger as the page moves.
function DropMenu({
  open,
  onClose,
  anchorRef,
  children,
  align = "left",
  maxHeight = 280,
  minWidth = 200,
}) {
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e) {
      if (ref.current && ref.current.contains(e.target)) return;
      if (anchorRef?.current && anchorRef.current.contains(e.target)) return;
      onClose();
    }
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, anchorRef]);

  const [portalStyle, setPortalStyle] = useState(null);
  useLayoutEffect(() => {
    if (!open || !anchorRef?.current) return;
    function reposition() {
      const r = anchorRef.current.getBoundingClientRect();
      // Clamp to the viewport: on a phone the chips wrap, so a menu anchored to one can run off either edge.
      const vw = window.innerWidth;
      const minW = Math.min(minWidth, vw - 16);
      const left = Math.max(8, Math.min(r.left, vw - 8 - minW));
      const right = Math.max(8, Math.min(vw - r.right, vw - 8 - minW));
      setPortalStyle({
        position: "fixed",
        top: r.bottom + 4,
        left: align === "right" ? undefined : left,
        right: align === "right" ? right : undefined,
        minWidth: minW,
        maxWidth: Math.min(320, vw - 8 - (align === "right" ? right : left)),
      });
    }
    reposition();
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [open, anchorRef, align, minWidth]);

  if (!open || !portalStyle) return null;
  return createPortal(
    <Menu ref={ref} style={portalStyle}>
      <MenuScroll style={{ maxHeight }}>{children}</MenuScroll>
    </Menu>,
    document.body,
  );
}

// A company's `region` is the countries it is hiring in: "Country A, Country B", most roles first.
const splitRegions = (region) =>
  typeof region === "string" ? region.split(",").map((r) => r.trim()).filter(Boolean) : [];

// Location trigger + anchored popover. Mirrors the Jobs page pattern:
// click toggles a popover anchored to the trigger, outside-click closes it.
function RegionsLocTrigger({ regions, accentColor, wrapperStyle }) {
  const list = Array.isArray(regions) ? regions.filter(Boolean) : [];
  const has = list.length > 0;
  const multi = list.length > 1;
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [open]);
  if (!has) return null;
  return (
    <span
      ref={ref}
      onClick={(e) => {
        if (!multi) return;
        e.stopPropagation();
        setOpen((v) => !v);
      }}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        cursor: multi ? "pointer" : "default",
        position: "relative",
        ...(wrapperStyle || {}),
      }}
      title={multi ? "Show all locations" : undefined}
    >
      <FiMapPin size={12} />
      {list[0]}
      {multi && (
        <span style={{ color: accentColor, marginLeft: 2 }}>
          +{list.length - 1}
        </span>
      )}
      {open && multi && (
        <RegionsPopover onClick={(e) => e.stopPropagation()}>
          <RegionsPopoverScroll>
            <div className="lp-head">{list.length} locations</div>
            {list.map((r, i) => (
              <div key={i} className="lp-row">
                {r}
              </div>
            ))}
          </RegionsPopoverScroll>
        </RegionsPopover>
      )}
    </span>
  );
}

// Friendly relative time for news/signals
function relativeTime(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return "";
    const diffMs = Date.now() - d.getTime();
    const sec = Math.round(diffMs / 1000);
    if (sec < 60) return "just now";
    const min = Math.round(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const days = Math.round(hr / 24);
    if (days < 7) return `${days}d ago`;
    if (days < 30) return `${Math.round(days / 7)}w ago`;
    if (days < 365) return `${Math.round(days / 30)}mo ago`;
    return `${Math.round(days / 365)}y ago`;
  } catch {
    return "";
  }
}

// ─── Skeleton placeholders ────────────────────────────────────────
// Each mirrors the real component's wrapper so layout height/columns are
// reserved exactly — no reflow when real data lands.

// One company row in the index list. Matches CompanyCard's grid + avatar size.
function CompanyCardSkeleton() {
  return (
    <CompanyCard as="div" style={{ cursor: "default" }}>
      <Skeleton width={56} height={56} radius="12px" />
      <CompanyInfo>
        <Skeleton width="42%" height={20} radius="6px" mb={8} />
        <div style={{ display: "flex", gap: 12 }}>
          <Skeleton width={90} height={13} radius="6px" />
          <Skeleton width={70} height={22} radius="6px" />
        </div>
      </CompanyInfo>
      <CompanyRowStats>
        {[0, 1, 2].map((i) => (
          <CompanyRowStat key={i}>
            <Skeleton width={40} height={22} radius="6px" mb={6} />
            <Skeleton width={56} height={10} radius="4px" />
          </CompanyRowStat>
        ))}
      </CompanyRowStats>
      <CompanyActions>
        <Skeleton width={104} height={36} radius="8px" />
      </CompanyActions>
    </CompanyCard>
  );
}

// Stat strip skeleton — 4 stat blocks matching FirmStat numerals/labels.
function FirmStatsSkeleton() {
  return (
    <FirmStats>
      {[0, 1, 2, 3].map((i) => (
        <FirmStat key={i}>
          <Skeleton width={80} height={38} radius="6px" mb={8} />
          <Skeleton width={100} height={12} radius="4px" mb={6} />
          <Skeleton width={130} height={12} radius="4px" />
        </FirmStat>
      ))}
    </FirmStats>
  );
}

// Card-head skeleton (title + sub) shared by every bento card.
function FirmCardHeadSkeleton({ withAction }) {
  return (
    <FirmCardHead>
      <div>
        <Skeleton width={160} height={22} radius="6px" mb={6} />
        <Skeleton width={120} height={12} radius="4px" />
      </div>
      {withAction && <Skeleton width={70} height={28} radius="99px" />}
    </FirmCardHead>
  );
}

// Placeholder firm cards while sources load; enough to fill the screen.
function FirmListSkeleton() {
  const ref = useRef(null);
  const count = useFillCount(ref, { min: 6 });
  return (
    <CardsContainer ref={ref} aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <CompanyCardSkeleton key={i} />
      ))}
    </CardsContainer>
  );
}

// A list of ExecRow-shaped placeholders (avatar + two lines + action).
function RowListSkeleton({ rows = 5 }) {
  return (
    <ExecList>
      {Array.from({ length: rows }).map((_, i) => (
        <ExecRow key={i} as="div">
          <Skeleton width={36} height={36} circle />
          <div style={{ minWidth: 0 }}>
            <Skeleton width="55%" height={13} radius="6px" mb={6} />
            <Skeleton width="80%" height={11} radius="4px" />
          </div>
          <Skeleton width={56} height={30} radius="8px" />
        </ExecRow>
      ))}
    </ExecList>
  );
}

// Full detail-view skeleton: header + stat strip + bento grid. Reserves the
// exact layout (incl. chart height) so the real content fades in without shift.
function DetailSkeleton() {
  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          margin: "20px 0 14px",
        }}
      >
        <Skeleton width={88} height={28} radius="7px" />
      </div>

      {/* Header card */}
      <FirmHead>
        <Skeleton width={72} height={72} radius="14px" />
        <div style={{ minWidth: 0 }}>
          <Skeleton width={280} height={36} radius="8px" mb={10} />
          <Skeleton width={220} height={13} radius="6px" mb={14} />
          <SkeletonText lines={2} height={13} gap={8} lastWidth="70%" />
          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <Skeleton width={90} height={26} radius="99px" />
            <Skeleton width={90} height={26} radius="99px" />
          </div>
        </div>
        <FirmHeadActions>
          <Skeleton width={84} height={36} radius="8px" />
          <Skeleton width={92} height={36} radius="8px" />
        </FirmHeadActions>
      </FirmHead>

      <FirmStatsSkeleton />
      <BentoSkeleton />
    </>
  );
}

// Bento grid skeleton — same 12-col spans + chart-height reservation as real.
function BentoSkeleton() {
  return (
    <FirmGrid>
      {/* Hiring trends (chart) */}
      <FirmCard $span={8}>
        <FirmCardHeadSkeleton withAction />
        <TrendRow>
          <TrendBullets>
            {[0, 1, 2, 3].map((i) => (
              <TrendBullet key={i}>
                <Skeleton width={48} height={24} radius="6px" mb={6} />
                <Skeleton width={70} height={11} radius="4px" />
              </TrendBullet>
            ))}
          </TrendBullets>
          {/* Reserve the exact 160px chart height so the chart never jumps. */}
          <TrendChartWrap>
            <Skeleton width="100%" height={160} radius="10px" />
          </TrendChartWrap>
        </TrendRow>
      </FirmCard>

      {/* Hiring by country (donut + list) */}
      <FirmCard $span={4}>
        <FirmCardHeadSkeleton />
        <CountryBody>
          <Skeleton width={170} height={170} circle />
          <CountryList>
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                style={{ display: "flex", alignItems: "center", gap: 10 }}
              >
                <Skeleton width={10} height={10} radius="2px" />
                <Skeleton width="60%" height={12} radius="4px" />
                <Skeleton
                  width={28}
                  height={12}
                  radius="4px"
                  style={{ marginLeft: "auto" }}
                />
              </div>
            ))}
          </CountryList>
        </CountryBody>
      </FirmCard>

      {/* HR & talent contacts */}
      <FirmCard $span={12}>
        <FirmCardHeadSkeleton />
        <RowListSkeleton rows={5} />
      </FirmCard>

      {/* Latest job postings */}
      <FirmCard $span={7}>
        <FirmCardHeadSkeleton withAction />
        <RowListSkeleton rows={6} />
      </FirmCard>

      {/* Recent signals */}
      <FirmCard $span={5}>
        <FirmCardHeadSkeleton withAction />
        <RowListSkeleton rows={4} />
      </FirmCard>
    </FirmGrid>
  );
}

const ResearchPage = () => {
  const { companyName } = useParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [sources, setSources] = useState([]);
  // Filter / sort state for the firm list
  const [filterCountry, setFilterCountry] = useState("all");
  const [filterIndustry, setFilterIndustry] = useState("all");
  const [hiringNow, setHiringNow] = useState(false);
  const [sortBy, setSortBy] = useState("az"); // az | active | jobs | contacts
  const [countryMenuOpen, setCountryMenuOpen] = useState(false);
  const [industryMenuOpen, setIndustryMenuOpen] = useState(false);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [cardsPage, setCardsPage] = useState(1);
  const cardsPerPage = 20;
  const [sourcesLoading, setSourcesLoading] = useState(true);
  const [inputFocused, setInputFocused] = useState(false);
  const [result, setResult] = useState(null);
  const [jobsList, setJobsList] = useState([]);
  const [peopleList, setPeopleList] = useState([]);
  const [scrapedNews, setScrapedNews] = useState([]);
  const [scrapedNewsTotal, setScrapedNewsTotal] = useState(0);
  const [scrapedNewsWindow, setScrapedNewsWindow] = useState(0);
  const [loading, setLoading] = useState(false);
  // Secondary-data gate: true while jobs/people/news/stats load AFTER the
  // research result lands, so the stat strip + bento grid show skeletons and
  // fade in together rather than each section trickling in independently.
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");
  const [savedCompanies, setSavedCompanies] = useState(new Set());
  // Statistics
  const [hiringTrends, setHiringTrends] = useState(null);
  const [countrySplit, setCountrySplit] = useState([]);
  // Full distinct count; countrySplit itself is only the top 10
  const [countryCount, setCountryCount] = useState(0);
  const [timeRange, setTimeRange] = useState("daily"); // daily | weekly | monthly
  const [chartData, setChartData] = useState(null);
  // HR contacts derived from peopleList — surface the HR list section on the
  // page. Clicking Email on a row navigates to /outreach for that person.
  const [hrCandidates, setHrCandidates] = useState([]);
  // On-demand HR-contact search (custom companies): country pick + in-flight flag.
  const [findHrCountry, setFindHrCountry] = useState("");
  const [findHrBusy, setFindHrBusy] = useState(false);
  // Modal state (Legal500 tiers).
  const [showAllTiers, setShowAllTiers] = useState(null);
  const [collapsedTiers, setCollapsedTiers] = useState(new Set());
  // Bookmarked jobs (by id) - from database
  const [bookmarks, setBookmarks] = useState([]);
  // Job detail modal state

  const { user, isAuthenticated } = useAuth();
  const { showToast } = useToast();

  // DropMenu (portalled) handles its own outside-click + Escape + scroll reposition.
  const countryChipRef = useRef(null);
  const industryChipRef = useRef(null);
  const sortChipRef = useRef(null);

  // suggestions filtered by current query (limit to 8). If empty, we won't show the popup.
  const filteredSuggestions =
    inputFocused && query.trim() !== ""
      ? sources
          .filter((s) => s.toLowerCase().includes(query.toLowerCase()))
          .slice(0, 8)
      : [];

  const [companyData, setCompanyData] = useState({}); // { companyName: { region, tiers, industry, counts } }

  useEffect(() => {
    let mounted = true;
    setSourcesLoading(true);
    // Same company set as the Jobs page's Tracked companies tab: scout-tracked + industry picks. Imported-job companies are excluded.
    fetchWithAuth("/api/sources")
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load sources");
        return res.json();
      })
      .then((data) => {
        if (!mounted) return;
        // Array of { name, region, tiers, industry, totalJobs, newThisWeek, contacts }
        if (Array.isArray(data)) {
          const items = [];
          const dataMap = {};

          data.forEach((item) => {
            const companyName = item.name || item.source || String(item);
            items.push(companyName);
            // Store full company data
            dataMap[companyName] = {
              region: item.region || null,
              tiers: item.tiers || {},
              industry: item.industry || null,
              totalJobs: item.totalJobs || 0,
              newThisWeek: item.newThisWeek || 0,
              contacts: item.contacts || 0,
            };
          });

          setSources(items);
          setCompanyData(dataMap);
        } else {
          setSources([]);
          setCompanyData({});
        }
      })
      .catch((err) => {
        console.error("Error loading sources", err);
        setError("Could not load available companies.");
        setSources([]);
        setCompanyData({});
      })
      .finally(() => mounted && setSourcesLoading(false));

    return () => {
      mounted = false;
    };
  }, []);

  // Infinite scroll: load more cards when scrolling near bottom
  useEffect(() => {
    if (result || sources.length === 0) return; // Don't add scroll listener if showing results or no sources

    const handleScroll = () => {
      // Check if user is near bottom of page (within 200px)
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      if (scrollTop + windowHeight >= documentHeight - 200) {
        const currentItems = cardsPage * cardsPerPage;
        if (currentItems < sources.length) {
          setCardsPage((prev) => prev + 1);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [cardsPage, sources.length, result, cardsPerPage]);

  // Initialize all tiers as collapsed when modal opens
  useEffect(() => {
    if (showAllTiers) {
      const tiers =
        result && result.tiers
          ? result.tiers
          : companyData[showAllTiers]
            ? companyData[showAllTiers].tiers
            : {};
      if (tiers && Object.keys(tiers).length > 0) {
        const allTierNumbers = Object.keys(tiers);
        setCollapsedTiers(new Set(allTierNumbers));
      }
    } else {
      // Reset when modal closes
      setCollapsedTiers(new Set());
    }
  }, [showAllTiers, result, companyData]);

  // Scroll restoration when returning from /jobs detail.
  // Pop one sticky target off sessionStorage; useLayoutEffect below
  // waits for the document to be tall enough before scrolling.
  const pendingScrollYRef = useRef(null);
  if (pendingScrollYRef.current === null) {
    try {
      const y = sessionStorage.getItem("research-scroll-y");
      if (y != null) {
        sessionStorage.removeItem("research-scroll-y");
        const n = parseInt(y, 10);
        pendingScrollYRef.current = Number.isFinite(n) ? n : 0;
      } else {
        pendingScrollYRef.current = 0;
      }
    } catch (_) {
      pendingScrollYRef.current = 0;
    }
  }
  useLayoutEffect(() => {
    const target = pendingScrollYRef.current;
    if (!target || target <= 0) return;
    const docH = document.documentElement.scrollHeight;
    const winH = window.innerHeight;
    if (docH - winH < target) return; // page not tall enough yet
    window.scrollTo({ top: target, behavior: "auto" });
    pendingScrollYRef.current = 0;
  }, [loading, result, hiringTrends]);

  // Auto-search when companyName is in URL
  useEffect(() => {
    // Skip the route-change scroll-to-top when we're restoring a saved
    // scroll position (returning from the Jobs detail view).
    if (!pendingScrollYRef.current) {
      window.scrollTo(0, 0);
    }

    if (companyName) {
      const decodedName = decodeURIComponent(companyName);
      // Perform search when companyName changes
      (async () => {
        if (!decodedName) return;
        setLoading(true);
        setDetailLoading(true);
        setError("");
        setResult(null);
        setHiringTrends(null);
        setCountrySplit([]);
        setShowAllTiers(null);
        try {
          const res = await fetchWithAuth(
            `/api/research?q=${encodeURIComponent(decodedName)}`,
          );
          if (res.status === 404) {
            const body = await res.json().catch(() => ({}));
            setResult(null);
            setError(body.error || "Not available in the database");
            setLoading(false);
            setDetailLoading(false);
            return;
          }
          if (!res.ok) throw new Error("Failed to fetch");
          const data = await res.json();
          // ensure the search box reflects the selected company
          setQuery(decodedName);

          // Set result immediately so company info shows right away
          setResult(data);
          setLoading(false);

          // fetch jobs, people, news, and stats in parallel in the background
          Promise.allSettled([
            (async () => {
              try {
                // Tracked jobs only: scout jobs for tracked companies, corpus jobs for
                // industry picks. The server gates the source to the caller's firms.
                const jobsRes = await fetchWithAuth(
                  `/api/jobs?source=${encodeURIComponent(decodedName)}&limit=1000&scope=mine&mineType=tracked`,
                );
                if (jobsRes.ok) {
                  const jobs = await jobsRes.json();
                  setJobsList(jobs);
                } else {
                  setJobsList([]);
                }
              } catch {
                setJobsList([]);
              }
            })(),
            (async () => {
              try {
                const peopleRes = await fetchWithAuth(
                  `/api/people?company=${encodeURIComponent(decodedName)}`,
                );
                if (peopleRes.ok) {
                  const people = await peopleRes.json();
                  setPeopleList(people || []);
                } else {
                  setPeopleList([]);
                }
              } catch {
                setPeopleList([]);
              }
            })(),
            (async () => {
              try {
                const windowStart = new Date(
                  Date.now() - SIGNAL_WINDOW_DAYS * 24 * 60 * 60 * 1000,
                ).toISOString();
                const newsRes = await fetchWithAuth(
                  `/api/scraped-news?jobs=${encodeURIComponent(
                    decodedName,
                  )}&countSince=${encodeURIComponent(windowStart)}`,
                );
                if (newsRes.ok) {
                  const news = await newsRes.json();
                  setScrapedNews(news || []);
                  // Both counts come from headers: the body is only a page, so
                  // its length is a page size rather than either total.
                  const windowHdr = newsRes.headers.get("X-Window-Count");
                  const totalHdr = newsRes.headers.get("X-Total-Count");
                  setScrapedNewsWindow(windowHdr != null ? Number(windowHdr) : 0);
                  setScrapedNewsTotal(
                    totalHdr != null ? Number(totalHdr) : (news || []).length,
                  );
                } else {
                  setScrapedNews([]);
                  setScrapedNewsTotal(0);
                  setScrapedNewsWindow(0);
                }
              } catch {
                setScrapedNews([]);
                setScrapedNewsTotal(0);
                setScrapedNewsWindow(0);
              }
            })(),
            // Fetch statistics from backend
            (async () => {
              try {
                // The server picks the scout or corpus collection from the firm itself.
                const statsRes = await fetchWithAuth(
                  `/api/stats?source=${encodeURIComponent(decodedName)}`,
                );
                if (statsRes.ok) {
                  const stats = await statsRes.json();

                  // Set hiring trends
                  if (stats.hiringTrends) {
                    setHiringTrends(stats.hiringTrends);
                  }

                  // Set chart data for graphs
                  if (stats.chartData) {
                    setChartData(stats.chartData);
                  }

                  // Set country split (top 10)
                  if (stats.byCountry) {
                    const countrySplit = stats.byCountry
                      .slice(0, 10)
                      .map((item) => ({
                        country: item._id || "Unknown",
                        count: item.count,
                      }));
                    setCountrySplit(countrySplit);
                    setCountryCount(stats.countryCount || countrySplit.length);
                  }

                }
              } catch (err) {
                console.error("Failed to fetch stats:", err);
              }
            })(),
          ])
            .catch((err) => {
              console.error("Error loading additional data:", err);
            })
            .finally(() => setDetailLoading(false));
        } catch (err) {
          setError(err.message || "An error occurred during search");
          setLoading(false);
          setDetailLoading(false);
        }
      })();
    } else {
      // Clear results when no companyName in URL
      setResult(null);
      setQuery("");
      setJobsList([]);
      setPeopleList([]);
      setError("");
      setHiringTrends(null);
      setCountrySplit([]);
      setDetailLoading(false);
    }
  }, [companyName]);

  // While a custom company is still being enriched, poll the profile and
  // refetch the cards whose stage just landed (people, news); the header
  // description/logo come back on the profile itself.
  useEffect(() => {
    if (!isEnriching(result)) return undefined;
    const decodedName = companyName ? decodeURIComponent(companyName) : "";
    if (!decodedName) return undefined;
    let cancelled = false;
    const startedAt = Date.now();
    let prev = result.enrichment;
    const refetchPeople = async () => {
      try {
        const r = await fetchWithAuth(
          `/api/people?company=${encodeURIComponent(decodedName)}`,
        );
        if (r.ok && !cancelled) setPeopleList((await r.json()) || []);
      } catch {
        /* keep what we have */
      }
    };
    const refetchNews = async () => {
      try {
        const windowStart = new Date(
          Date.now() - SIGNAL_WINDOW_DAYS * 24 * 60 * 60 * 1000,
        ).toISOString();
        const r = await fetchWithAuth(
          `/api/scraped-news?jobs=${encodeURIComponent(
            decodedName,
          )}&countSince=${encodeURIComponent(windowStart)}`,
        );
        if (!r.ok || cancelled) return;
        const news = await r.json();
        setScrapedNews(news || []);
        const windowHdr = r.headers.get("X-Window-Count");
        const totalHdr = r.headers.get("X-Total-Count");
        setScrapedNewsWindow(windowHdr != null ? Number(windowHdr) : 0);
        setScrapedNewsTotal(
          totalHdr != null ? Number(totalHdr) : (news || []).length,
        );
      } catch {
        /* keep what we have */
      }
    };
    const timer = setInterval(async () => {
      if (Date.now() - startedAt > ENRICH_POLL_MAX_MS) {
        clearInterval(timer);
        return;
      }
      try {
        const r = await fetchWithAuth(
          `/api/research?q=${encodeURIComponent(decodedName)}`,
        );
        if (!r.ok || cancelled) return;
        const data = await r.json();
        const next = data.enrichment || {};
        if (next.people === "done" && prev.people !== "done") refetchPeople();
        if (next.news === "done" && prev.news !== "done") refetchNews();
        prev = next;
        setResult(data);
      } catch {
        /* transient; try again next tick */
      }
    }, ENRICH_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [companyName, result?.enrichment?.status]);

  // load current user's saved companies so we can show contextual actions
  useEffect(() => {
    let mounted = true;
    if (!user?.sub) return;
    (async () => {
      try {
        const doc = await getUserSettings(user.sub);
        if (!mounted) return;
        setSavedCompanies(
          new Set(Array.isArray(doc?.companies) ? doc.companies : []),
        );
      } catch (err) {
        if (!mounted) return;
        setSavedCompanies(new Set());
      }
    })();
    return () => {
      mounted = false;
    };
  }, [user?.sub]);

  // Load user bookmarks
  useEffect(() => {
    if (!isAuthenticated || !user?.sub) {
      setBookmarks([]);
      return;
    }

    const loadUserSettings = async () => {
      try {
        const data = await getUserSettings(user.sub);
        if (!data) return;
        const watchlistItems = data.watchlist || [];
        const jobIds = watchlistItems.map((item) =>
          typeof item.job_id === "object" && item.job_id.$oid
            ? item.job_id.$oid
            : item.job_id,
        );
        setBookmarks(jobIds);
      } catch (err) {
        console.error("Failed to load user settings:", err);
      }
    };

    loadUserSettings();
  }, [isAuthenticated, user?.sub]);

  // Save user settings to database
  const saveUserSettings = async (updatedBookmarks) => {
    if (!isAuthenticated || !user) return;

    try {
      const watchlist = updatedBookmarks.map((jobId) => ({
        job_id: jobId,
        filter_id: null,
      }));

      // Update the shared cache before the POST so the Saved page sees this immediately.
      updateCachedUserSettings(user.sub, { watchlist });
      await fetchWithAuth(
        `/api/user-settings/${encodeURIComponent(user.sub)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            watchlist,
          }),
        },
      );
      invalidateUserSettingsCache(user.sub);
    } catch (err) {
      console.error("Failed to save user settings:", err);
    }
  };

  // Toggle bookmark
  const toggleBookmark = (job) => {
    const id = job._id?.$oid || job._id;

    if (bookmarks.includes(id)) {
      const newBookmarks = bookmarks.filter((bid) => bid !== id);
      setBookmarks(newBookmarks);
      saveUserSettings(newBookmarks);
    } else {
      const newBookmarks = [...bookmarks, id];
      setBookmarks(newBookmarks);
      saveUserSettings(newBookmarks);
    }
  };

  // derive HR candidates whenever peopleList changes
  useEffect(() => {
    try {
      const list = Array.isArray(peopleList) ? peopleList : [];
      // Match the structured category, not the free-text position. The old
      // keyword regex used word boundaries, so it silently dropped "Recruiter",
      // "Talents", "HRIS" and "HRBP" — real HR contacts that never reached
      // outreach because their job title carried a suffix or a plural.
      const candidates = list.filter(
        (p) => String(p?.position_category || "").trim().toUpperCase() === "HR",
      );
      setHrCandidates(candidates);
    } catch {
      setHrCandidates([]);
    }
  }, [peopleList]);

  // Queue an on-demand HR-contact search for this (custom) company, scoped to
  // one country. The enrichment poller then refetches people when it lands.
  const queueFindHr = async (country) => {
    const decodedName = companyName ? decodeURIComponent(companyName) : "";
    if (!decodedName || findHrBusy) return;
    setFindHrBusy(true);
    try {
      const r = await fetchWithAuth("/api/research/find-hr", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ companyName: decodedName, country: country || "" }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok)
        throw new Error(data?.error || "Failed to start the contact search");
      // The poller keys off enrichment.status — flip it locally so it starts now.
      setResult((prev) =>
        prev
          ? {
              ...prev,
              enrichment: {
                ...(prev.enrichment || {}),
                status: "queued",
                people: "queued",
              },
            }
          : prev,
      );
      showToast &&
        showToast(
          country
            ? `Looking for HR contacts in ${country}…`
            : "Looking for HR contacts…",
        );
    } catch (err) {
      showToast &&
        showToast(err.message || "Failed to start the contact search", {
          duration: 3500,
        });
    } finally {
      setFindHrBusy(false);
    }
  };

  // Toggle Save for a company — saved companies are auto-watched for new jobs.
  // Stored as the `companies` array in user_settings.
  async function toggleSavedCompany(company) {
    if (!user?.sub) return;
    const next = new Set(savedCompanies);
    if (next.has(company)) next.delete(company);
    else next.add(company);
    setSavedCompanies(next);
    try {
      updateCachedUserSettings(user.sub, { companies: Array.from(next) });
      await fetchWithAuth(
        `/api/user-settings/${encodeURIComponent(user.sub)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ companies: Array.from(next) }),
        },
      );
      invalidateUserSettingsCache(user.sub);
    } catch (err) {
      // revert on failure
      setSavedCompanies(savedCompanies);
    }
  }

  // Compose: go to /outreach scoped to everyone from this company.
  // Compose happens there — no local modal.
  function openOutreachForCompany(company) {
    if (!company) return;
    navigate(`/outreach?tab=clients&company=${encodeURIComponent(company)}`);
  }

  // HR person: open the Clients tab on /outreach with their profile open.
  function openOutreachForPerson(person) {
    if (!person) return;
    const id = person._id?.$oid || person._id || person.id;
    if (!id) {
      navigate(`/outreach?tab=clients`);
      return;
    }
    navigate(
      `/outreach?tab=clients&personId=${encodeURIComponent(id)}`,
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    await doSearch(query);
  };

  // Programmatic search helper that accepts a company/query string so callers
  // can navigate directly to a company result without waiting for state updates.
  const doSearch = async (q) => {
    if (!q) return;
    // Navigate to the research route with company name
    navigate(`/research/${encodeURIComponent(q)}`);
  };

  // ── Derived: filter options + filtered/sorted source list ───────
  // Live totals for the hero strip, summed from the same per-company data the index lists.
  const openRoles = React.useMemo(
    () => sources.reduce((n, name) => n + (companyData[name]?.totalJobs || 0), 0),
    [sources, companyData],
  );

  const allCountries = React.useMemo(() => {
    const s = new Set();
    sources.forEach((name) => {
      splitRegions(companyData[name]?.region).forEach((r) => s.add(r));
    });
    return Array.from(s).sort();
  }, [sources, companyData]);

  const allIndustries = React.useMemo(() => {
    const s = new Set();
    sources.forEach((name) => {
      const info = companyData[name];
      const cat = info && info.industry;
      if (cat) s.add(String(cat).trim());
    });
    return Array.from(s).sort();
  }, [sources, companyData]);

  const filteredSources = React.useMemo(() => {
    const norm = (s) => String(s || "").toLowerCase();
    const q = norm(query);
    const list = sources.filter((name) => {
      const info = companyData[name] || {};
      if (q && !norm(name).includes(q)) return false;
      if (filterCountry !== "all" && !splitRegions(info.region).includes(filterCountry))
        return false;
      if (filterIndustry !== "all" && info.industry !== filterIndustry)
        return false;
      if (hiringNow && !(info.totalJobs > 0)) return false;
      return true;
    });
    const get = (n, k) => (companyData[n] && companyData[n][k]) || 0;
    list.sort((a, b) => {
      if (sortBy === "active") {
        return (
          get(b, "newThisWeek") - get(a, "newThisWeek") ||
          get(b, "totalJobs") - get(a, "totalJobs")
        );
      }
      if (sortBy === "jobs") return get(b, "totalJobs") - get(a, "totalJobs");
      if (sortBy === "contacts")
        return get(b, "contacts") - get(a, "contacts");
      return a.localeCompare(b);
    });
    return list;
  }, [sources, companyData, query, filterCountry, filterIndustry, hiringNow, sortBy]);

  const sortLabel = {
    active: "Most active",
    jobs: "Most jobs",
    contacts: "Most contacts",
    az: "A–Z",
  }[sortBy];

  // While the research fetch is in flight on a deep-link / selection, show the
  // full detail skeleton in place of an empty page (no full-page overlay).
  const showDetailSkeleton = loading && companyName;

  return (
    <>
      <Container>
        {/* ─── DETAIL SKELETON (deep-link / selection in flight) ─── */}
        {showDetailSkeleton && <DetailSkeleton />}

        {/* ─── INDEX (no company selected) ─── */}
        {!result && !showDetailSkeleton && (
          <>
            <Hero>
              <div>
                <HeroEyebrow>
                  <Spark size={12} /> AI-powered intelligence
                </HeroEyebrow>
                <HeroTitle>Company Research</HeroTitle>
                <HeroSub>
                  Deep-dive any law firm worldwide. Live hiring signals,
                  leadership changes, HR & talent contacts — ready for outreach.
                </HeroSub>
              </div>
              <HeroStats>
                <HeroStat>
                  <HeroStatNum>{formatNumber(sources.length)}</HeroStatNum>
                  <HeroStatLbl>Firms tracked</HeroStatLbl>
                </HeroStat>
                <HeroStat>
                  <HeroStatNum>{formatNumber(allCountries.length)}</HeroStatNum>
                  <HeroStatLbl>Countries</HeroStatLbl>
                </HeroStat>
                <HeroStat>
                  <HeroStatNum>{formatNumber(openRoles)}</HeroStatNum>
                  <HeroStatLbl>Open roles</HeroStatLbl>
                </HeroStat>
              </HeroStats>
            </Hero>

            <SearchRow data-tour="research-search">
              <SearchForm onSubmit={handleSubmit}>
                {sourcesLoading || sources.length === 0 ? (
                  <InputWrapper>
                    <SearchIconWrapper>
                      <FiSearch size={16} />
                    </SearchIconWrapper>
                    <Input
                      value=""
                      disabled
                      placeholder={
                        sourcesLoading
                          ? "Loading…"
                          : "No companies in My jobs yet — add some on the Jobs page"
                      }
                    />
                  </InputWrapper>
                ) : (
                  <InputWrapper>
                    <SearchIconWrapper>
                      <FiSearch size={16} />
                    </SearchIconWrapper>
                    <Input
                      type="text"
                      placeholder="Search by company name…"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      onFocus={() => setInputFocused(true)}
                      onBlur={() =>
                        setTimeout(() => setInputFocused(false), 150)
                      }
                      autoComplete="off"
                      required
                    />
                    <SearchButton
                      type="submit"
                      disabled={loading || sourcesLoading || !query}
                    >
                      <Spark size={14} />
                      <span>{loading ? "Searching…" : "Search"}</span>
                    </SearchButton>
                    {filteredSuggestions.length > 0 && (
                      <SuggestionList>
                        {filteredSuggestions.map((s, idx) => (
                          <SuggestionItem
                            key={idx}
                            onMouseDown={() => {
                              setQuery(s);
                              setInputFocused(false);
                            }}
                          >
                            {s}
                          </SuggestionItem>
                        ))}
                      </SuggestionList>
                    )}
                  </InputWrapper>
                )}
              </SearchForm>

              <ChipFilters>
                <Chip
                  ref={countryChipRef}
                  $active={filterCountry !== "all"}
                  onClick={() => {
                    setCountryMenuOpen((v) => !v);
                    setIndustryMenuOpen(false);
                    setSortMenuOpen(false);
                  }}
                >
                  <FiGlobe size={12} />
                  {filterCountry === "all" ? "Country" : filterCountry}
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M6 9l6 6 6-6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Chip>
                <DropMenu
                  open={countryMenuOpen}
                  onClose={() => setCountryMenuOpen(false)}
                  anchorRef={countryChipRef}
                >
                  <MenuItem
                    onClick={() => {
                      setFilterCountry("all");
                      setCountryMenuOpen(false);
                    }}
                  >
                    All countries
                  </MenuItem>
                  <MenuDivider />
                  {allCountries.length === 0 && (
                    <MenuItem style={{ opacity: 0.6, cursor: "default" }}>
                      No countries
                    </MenuItem>
                  )}
                  {allCountries.map((c) => (
                    <MenuItem
                      key={c}
                      onClick={() => {
                        setFilterCountry(c);
                        setCountryMenuOpen(false);
                      }}
                    >
                      {c}
                      {filterCountry === c && (
                        <FiCheck size={12} style={{ marginLeft: "auto" }} />
                      )}
                    </MenuItem>
                  ))}
                </DropMenu>

                <Chip
                  ref={industryChipRef}
                  $active={filterIndustry !== "all"}
                  onClick={() => {
                    setIndustryMenuOpen((v) => !v);
                    setCountryMenuOpen(false);
                    setSortMenuOpen(false);
                  }}
                >
                  <FiTag size={12} />
                  {filterIndustry === "all" ? "Industry" : filterIndustry}
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M6 9l6 6 6-6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Chip>
                <DropMenu
                  open={industryMenuOpen}
                  onClose={() => setIndustryMenuOpen(false)}
                  anchorRef={industryChipRef}
                  minWidth={220}
                >
                  <MenuItem
                    onClick={() => {
                      setFilterIndustry("all");
                      setIndustryMenuOpen(false);
                    }}
                  >
                    All industries
                  </MenuItem>
                  <MenuDivider />
                  {allIndustries.length === 0 && (
                    <MenuItem style={{ opacity: 0.6, cursor: "default" }}>
                      No industries
                    </MenuItem>
                  )}
                  {allIndustries.map((s) => (
                    <MenuItem
                      key={s}
                      onClick={() => {
                        setFilterIndustry(s);
                        setIndustryMenuOpen(false);
                      }}
                    >
                      {s}
                      {filterIndustry === s && (
                        <FiCheck size={12} style={{ marginLeft: "auto" }} />
                      )}
                    </MenuItem>
                  ))}
                </DropMenu>

                <Chip
                  $active={hiringNow}
                  onClick={() => setHiringNow((v) => !v)}
                >
                  <Spark size={12} />
                  Hiring now
                </Chip>

                <Chip
                  ref={sortChipRef}
                  onClick={() => {
                    setSortMenuOpen((v) => !v);
                    setCountryMenuOpen(false);
                    setIndustryMenuOpen(false);
                  }}
                >
                  Sort: {sortLabel}
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M6 9l6 6 6-6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Chip>
                <DropMenu
                  open={sortMenuOpen}
                  onClose={() => setSortMenuOpen(false)}
                  anchorRef={sortChipRef}
                  align="right"
                >
                  {[
                    ["az", "A–Z"],
                    ["active", "Most active"],
                    ["jobs", "Most jobs"],
                    ["contacts", "Most contacts"],
                  ].map(([key, label]) => (
                    <MenuItem
                      key={key}
                      onClick={() => {
                        setSortBy(key);
                        setSortMenuOpen(false);
                      }}
                    >
                      {label}
                      {sortBy === key && (
                        <FiCheck size={12} style={{ marginLeft: "auto" }} />
                      )}
                    </MenuItem>
                  ))}
                </DropMenu>
              </ChipFilters>
            </SearchRow>

            {sources.length > 0 && (
              <CountLine>
                <b>{formatNumber(filteredSources.length)}</b> of{" "}
                {formatNumber(sources.length)} firms
              </CountLine>
            )}

            {error && (
              <div
                style={{
                  color: "var(--danger)",
                  background: "var(--danger-soft)",
                  padding: "10px 14px",
                  borderRadius: 8,
                  marginBottom: 12,
                  fontSize: 13,
                }}
              >
                {error}
              </div>
            )}

            {/* Firm list — skeleton rows while sources load (reserve layout) */}
            {sourcesLoading && <FirmListSkeleton />}

            {/* Firm list */}
            {!sourcesLoading && sources.length > 0 && filteredSources.length === 0 && (
              <div
                style={{
                  padding: "40px 20px",
                  textAlign: "center",
                  color: TOKENS.inkMuted,
                  fontSize: 14,
                  background: TOKENS.surface,
                  border: `1px solid ${TOKENS.line}`,
                  borderRadius: 14,
                }}
              >
                No firms match your filters.{" "}
                <button
                  onClick={() => {
                    setQuery("");
                    setFilterCountry("all");
                    setFilterIndustry("all");
                    setHiringNow(false);
                  }}
                  style={{
                    background: "transparent",
                    border: 0,
                    color: TOKENS.accent,
                    cursor: "pointer",
                    fontSize: 14,
                    fontWeight: 500,
                    padding: 0,
                  }}
                >
                  Clear filters
                </button>
              </div>
            )}
            {sources.length > 0 && filteredSources.length > 0 && (
              <FadeIn>
              <CardsContainer>
                {filteredSources
                  .slice(0, cardsPage * cardsPerPage)
                  .map((company, idx) => {
                    const companyInfo = companyData[company] || {};
                    const industry = companyInfo.industry;
                    const firstLetter = company.charAt(0).toUpperCase();
                    const regions = splitRegions(companyInfo.region);
                    const firstRegion = regions[0] || "";
                    const remainingRegionsCount = regions.length - 1;

                    return (
                      <CompanyCard
                        key={company + idx}
                        onClick={() => doSearch(company)}
                        data-tour={idx === 0 ? "research-first" : undefined}
                      >
                        <CompanyLogo
                          name={company}
                          size={56}
                          radius={12}
                          bg={TOKENS.bg2}
                          fg={TOKENS.ink}
                          fallback={firstLetter}
                          style={{ boxShadow: TOKENS.shadowSm }}
                        />
                        <CompanyInfo>
                          <CompanyTitle>{company}</CompanyTitle>
                          <CompanyDetails>
                            {firstRegion && (
                              <CompanyDetailItem>
                                <FiMapPin size={12} />
                                <span>{firstRegion}</span>
                                {remainingRegionsCount > 0 && (
                                  <span
                                    style={{
                                      color: TOKENS.accent,
                                      marginLeft: 2,
                                    }}
                                  >
                                    +{remainingRegionsCount}
                                  </span>
                                )}
                              </CompanyDetailItem>
                            )}
                            {industry && (
                              <PillTag $hideOnMobile>
                                {industry}
                              </PillTag>
                            )}
                          </CompanyDetails>
                        </CompanyInfo>
                        <CompanyRowStats>
                          <CompanyRowStat>
                            <CompanyRowStatNum>
                              {formatNumber(companyInfo.totalJobs || 0)}
                            </CompanyRowStatNum>
                            <CompanyRowStatLbl>Open roles</CompanyRowStatLbl>
                          </CompanyRowStat>
                          <CompanyRowStat>
                            <CompanyRowStatNum $accent>
                              +{formatNumber(companyInfo.newThisWeek || 0)}
                            </CompanyRowStatNum>
                            <CompanyRowStatLbl>This week</CompanyRowStatLbl>
                          </CompanyRowStat>
                          <CompanyRowStat>
                            <CompanyRowStatNum>
                              {formatNumber(companyInfo.contacts || 0)}
                            </CompanyRowStatNum>
                            <CompanyRowStatLbl>Contacts</CompanyRowStatLbl>
                          </CompanyRowStat>
                        </CompanyRowStats>
                        <CompanyActions>
                          <ResearchButton
                            onClick={(e) => {
                              e.stopPropagation();
                              doSearch(company);
                            }}
                          >
                            <span>Research</span>
                            <FiArrowRight size={12} />
                          </ResearchButton>
                        </CompanyActions>
                      </CompanyCard>
                    );
                  })}
              </CardsContainer>
              </FadeIn>
            )}
          </>
        )}

        {/* ─── DETAIL (a company is selected) ─── */}
        {result &&
          (() => {
            const companyName = (result.title || query || "").trim();
            const regions = splitRegions(result.region);

            const hrList = Array.isArray(hrCandidates)
              ? hrCandidates.slice(0, 5)
              : [];
            // peopleList is unpaginated, so this is a true spread, not a page
            const contactCountries = new Set(
              peopleList.map((p) => String(p?.country || "").trim()).filter(Boolean),
            ).size;
            const recentJobs = jobsList.slice(0, 6);
            const newsList = scrapedNews.slice(0, 6);

            const t = TOKENS;

            return (
              <>
                <FadeIn>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    margin: "20px 0 14px",
                  }}
                >
                  <BackButton onClick={() => navigate("/research")}>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15 18l-6-6 6-6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    All firms
                  </BackButton>
                </div>

                {/* Header card */}
                <FirmHead data-tour="research-head">
                  <CompanyLogo
                    name={companyName}
                    size={72}
                    radius={14}
                    bg={t.bg2}
                    fg={t.ink}
                    fallback={companyName.charAt(0).toUpperCase()}
                    style={{ boxShadow: t.shadowSm }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        flexWrap: "wrap",
                      }}
                    >
                      <FirmHeadName>{result.title || query}</FirmHeadName>
                      {result.tiers &&
                        Object.keys(result.tiers).length > 0 && (
                          <PillTag
                            style={{
                              background: t.accentSoft,
                              color: t.accentInk,
                            }}
                          >
                            {Object.keys(result.tiers).length} Legal500 tiers
                          </PillTag>
                        )}
                      {isEnriching(result) && (
                        <PillTag
                          title="We're still gathering this company's profile, contacts and news."
                          style={{
                            background: t.accentSoft,
                            color: t.accentInk,
                          }}
                        >
                          Building profile…
                        </PillTag>
                      )}
                    </div>
                    <FirmHeadMeta>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                        }}
                      >
                        <Building size={12} />{" "}
                        {result.industry ||
                          (result.source === "custom" ? "Company" : "Law firm")}
                      </span>
                      {regions.length > 0 && (
                        <RegionsLocTrigger
                          regions={regions}
                          accentColor={t.accent}
                        />
                      )}
                      {hiringTrends && (
                        <span>
                          {formatNumber(hiringTrends.total)} roles tracked
                        </span>
                      )}
                    </FirmHeadMeta>
                    {result.description && (
                      <FirmHeadDesc>{result.description}</FirmHeadDesc>
                    )}
                    <FirmHeadRow>
                      {result.website_link && (
                        <FirmHeadLink
                          href={result.website_link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FiLink size={12} /> Website
                        </FirmHeadLink>
                      )}
                      {result.linkedin_link && (
                        <FirmHeadLink
                          href={result.linkedin_link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <BrandLinkedIn size={12} /> LinkedIn
                        </FirmHeadLink>
                      )}
                      {result.twitter_link && (
                        <FirmHeadLink
                          href={result.twitter_link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <BrandX size={12} /> X (Twitter)
                        </FirmHeadLink>
                      )}
                    </FirmHeadRow>

                    {/* Action buttons (Legal500, Compose) */}
                    {companyName && (
                      <ActionButtonsContainer>
                        {result.tiers &&
                          typeof result.tiers === "object" &&
                          Object.keys(result.tiers).length > 0 && (
                            <IconButtonWrapper>
                              <IconButton
                                aria-label={`View Legal500 ranking for ${companyName}`}
                                onClick={() =>
                                  setShowAllTiers(result.title || query)
                                }
                              >
                                <Trophy size={14} />
                              </IconButton>
                              <Tooltip>Legal500 rankings</Tooltip>
                            </IconButtonWrapper>
                          )}
                        <IconButtonWrapper>
                          <IconButton
                            aria-label={`Compose outreach to ${companyName}`}
                            onClick={() => openOutreachForCompany(companyName)}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </IconButton>
                          <Tooltip>Compose outreach</Tooltip>
                        </IconButtonWrapper>
                      </ActionButtonsContainer>
                    )}
                  </div>
                  <FirmHeadActions>
                    <ButtonGhost
                      onClick={() => toggleSavedCompany(companyName)}
                      style={
                        savedCompanies.has(companyName)
                          ? {
                              background: t.accentSoft,
                              borderColor: t.accent,
                              color: t.accent,
                            }
                          : undefined
                      }
                    >
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill={
                          savedCompanies.has(companyName)
                            ? "currentColor"
                            : "none"
                        }
                      >
                        <path
                          d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      {savedCompanies.has(companyName) ? "Saved" : "Save"}
                    </ButtonGhost>
                  </FirmHeadActions>
                </FirmHead>
                </FadeIn>

                {/* Stat strip + bento grid — gated together on the secondary
                    data fetch so they reveal as one unit (no trickle-in). */}
                {detailLoading ? (
                  <>
                    <FirmStatsSkeleton />
                    <BentoSkeleton />
                  </>
                ) : (
                  <FadeIn>
                {/* Stat strip */}
                <FirmStats data-tour="research-stats">
                  <FirmStat>
                    <FirmStatNum>
                      {formatNumber(
                        hiringTrends?.total ?? jobsList.length ?? 0,
                      )}
                    </FirmStatNum>
                    <FirmStatLbl>Open roles</FirmStatLbl>
                    <FirmStatSub>
                      across {countryCount || 0} countries
                    </FirmStatSub>
                  </FirmStat>
                  <FirmStat>
                    <FirmStatNum $accent>
                      +{formatNumber(hiringTrends?.last24h ?? 0)}
                    </FirmStatNum>
                    <FirmStatLbl>New today</FirmStatLbl>
                    <FirmStatSub>
                      {formatNumber(hiringTrends?.last7d ?? 0)} this week ·{" "}
                      {formatNumber(hiringTrends?.last30d ?? 0)} this month
                    </FirmStatSub>
                  </FirmStat>
                  <FirmStat>
                    <FirmStatNum>{formatNumber(peopleList.length)}</FirmStatNum>
                    <FirmStatLbl>Mapped contacts</FirmStatLbl>
                    <FirmStatSub>
                      across {formatNumber(contactCountries)}{" "}
                      {contactCountries === 1 ? "country" : "countries"}
                    </FirmStatSub>
                  </FirmStat>
                  <FirmStat>
                    <FirmStatNum>
                      {formatNumber(scrapedNewsWindow)}
                      {scrapedNewsWindow > 0 && <PulseDot />}
                    </FirmStatNum>
                    <FirmStatLbl>Signals this month</FirmStatLbl>
                    <FirmStatSub>
                      last {SIGNAL_WINDOW_DAYS} days
                      {newsList[0]?.published
                        ? ` · latest ${relativeTime(newsList[0].published)}`
                        : ""}
                    </FirmStatSub>
                  </FirmStat>
                </FirmStats>

                {/* Bento grid */}
                <FirmGrid>
                  {/* Hiring trends */}
                  <FirmCard $span={8} data-tour="research-trends">
                    <FirmCardHead>
                      <div>
                        <FirmCardH>Hiring trends</FirmCardH>
                        <FirmCardSub>Job postings over time</FirmCardSub>
                      </div>
                      <Seg>
                        <SegBtn
                          $active={timeRange === "daily"}
                          onClick={() => setTimeRange("daily")}
                        >
                          30d
                        </SegBtn>
                        <SegBtn
                          $active={timeRange === "weekly"}
                          onClick={() => setTimeRange("weekly")}
                        >
                          12w
                        </SegBtn>
                        <SegBtn
                          $active={timeRange === "monthly"}
                          onClick={() => setTimeRange("monthly")}
                        >
                          12m
                        </SegBtn>
                      </Seg>
                    </FirmCardHead>
                    <TrendRow>
                      <TrendBullets>
                        <TrendBullet>
                          <TrendBulletNum>
                            {formatNumber(hiringTrends?.last24h ?? 0)}
                          </TrendBulletNum>
                          <TrendBulletLbl>last 24h</TrendBulletLbl>
                        </TrendBullet>
                        <TrendBullet>
                          <TrendBulletNum>
                            {formatNumber(hiringTrends?.last7d ?? 0)}
                          </TrendBulletNum>
                          <TrendBulletLbl>last 7 days</TrendBulletLbl>
                        </TrendBullet>
                        <TrendBullet>
                          <TrendBulletNum>
                            {formatNumber(hiringTrends?.last30d ?? 0)}
                          </TrendBulletNum>
                          <TrendBulletLbl>last 30 days</TrendBulletLbl>
                        </TrendBullet>
                        <TrendBullet>
                          <TrendBulletNum>
                            {formatNumber(hiringTrends?.last365d ?? 0)}
                          </TrendBulletNum>
                          <TrendBulletLbl>last 12 months</TrendBulletLbl>
                        </TrendBullet>
                      </TrendBullets>
                      <TrendChartWrap style={{ color: t.inkMuted }}>
                        {chartData?.[timeRange]?.length > 0 ? (
                          <LineChartSVG
                            data={chartData[timeRange].map((p) => ({
                              label: p.date || p.label || "",
                              value: p.count || 0,
                            }))}
                            color={t.accent}
                            tooltipColor={{
                              bg:
                                "var(--ink)",
                              fg: "var(--on-fill)",
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              display: "grid",
                              placeItems: "center",
                              height: "100%",
                              color: t.inkMuted,
                              fontSize: 13,
                            }}
                          >
                            No data
                          </div>
                        )}
                      </TrendChartWrap>
                    </TrendRow>
                  </FirmCard>

                  {/* Hiring by country */}
                  <FirmCard $span={4}>
                    <FirmCardHead>
                      <div>
                        <FirmCardH>Hiring by country</FirmCardH>
                        <FirmCardSub>
                          {countryCount} countries
                        </FirmCardSub>
                      </div>
                    </FirmCardHead>
                    {countrySplit.length > 0 ? (
                      <CountryBody>
                        <DonutChartSVG
                          data={countrySplit}
                          total={hiringTrends?.total ?? jobsList.length}
                          size={170}
                          thickness={28}
                          inkColor={t.ink}
                          mutedColor={t.inkMuted}
                        />
                        <CountryList>
                          {countrySplit.slice(0, 8).map((c, i) => (
                            <CountryRow key={c.country + i}>
                              <CountryDot
                                $color={
                                  DONUT_PALETTE[i % DONUT_PALETTE.length]
                                }
                              />
                              <CountryName>
                                {c.country}
                              </CountryName>
                              <CountryNum>
                                {formatNumber(c.count)}
                              </CountryNum>
                            </CountryRow>
                          ))}
                          {countryCount > 8 && (
                            <CountryRow
                              style={{
                                color: t.inkMuted,
                                fontStyle: "italic",
                              }}
                            >
                              <span />
                              <span>
                                + {countryCount - 8} more countries
                              </span>
                              <span />
                            </CountryRow>
                          )}
                        </CountryList>
                      </CountryBody>
                    ) : (
                      <div
                        style={{
                          padding: "30px 0",
                          textAlign: "center",
                          color: t.inkMuted,
                          fontSize: 13,
                        }}
                      >
                        No data available.
                      </div>
                    )}
                  </FirmCard>

                  {/* HR & talent contacts */}
                  <FirmCard $span={12} data-tour="research-contacts">
                    <FirmCardHead>
                      <div>
                        <FirmCardH>HR & talent contacts</FirmCardH>
                        <FirmCardSub>
                          {formatNumber(hrCandidates.length)} mapped · ready for
                          outreach
                        </FirmCardSub>
                      </div>
                      {hrCandidates.length > 0 && (
                        <ButtonGhost
                          style={{
                            height: 30,
                            padding: "0 12px",
                            fontSize: 12.5,
                          }}
                          onClick={() =>
                            navigate(
                              `/outreach?tab=clients&company=${encodeURIComponent(
                                companyName,
                              )}`,
                            )
                          }
                        >
                          View all
                          <FiChevronRight size={12} />
                        </ButtonGhost>
                      )}
                    </FirmCardHead>
                    {hrList.length === 0 ? (
                      result?.source === "custom" &&
                      ["queued", "running"].includes(
                        result?.enrichment?.people,
                      ) ? (
                        <div
                          style={{
                            padding: "30px 0",
                            textAlign: "center",
                            color: t.inkMuted,
                            fontSize: 13,
                          }}
                        >
                          Mapping HR contacts… this can take a minute.
                        </div>
                      ) : result?.source === "custom" ? (
                        <div style={{ padding: "24px 0", textAlign: "center" }}>
                          <div
                            style={{
                              color: t.inkMuted,
                              fontSize: 13,
                              marginBottom: 12,
                            }}
                          >
                            No HR contacts mapped yet.
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              gap: 8,
                              flexWrap: "wrap",
                            }}
                          >
                            {countrySplit.length > 0 && (
                              <select
                                value={
                                  findHrCountry ||
                                  countrySplit[0]?.country ||
                                  ""
                                }
                                onChange={(e) =>
                                  setFindHrCountry(e.target.value)
                                }
                                aria-label="Country to search"
                                style={{
                                  height: 30,
                                  padding: "0 8px",
                                  fontSize: 12.5,
                                  color: t.ink,
                                  background: t.surface,
                                  border: `1px solid ${t.line}`,
                                  borderRadius: 8,
                                }}
                              >
                                {countrySplit.map((c) => (
                                  <option key={c.country} value={c.country}>
                                    {c.country}
                                  </option>
                                ))}
                              </select>
                            )}
                            <ButtonGhost
                              disabled={findHrBusy}
                              style={{
                                height: 30,
                                padding: "0 12px",
                                fontSize: 12.5,
                              }}
                              onClick={() =>
                                queueFindHr(
                                  findHrCountry ||
                                    countrySplit[0]?.country ||
                                    "",
                                )
                              }
                            >
                              {findHrBusy ? "Starting…" : "Find HR contacts"}
                            </ButtonGhost>
                          </div>
                        </div>
                      ) : (
                        <div
                          style={{
                            padding: "30px 0",
                            textAlign: "center",
                            color: t.inkMuted,
                            fontSize: 13,
                          }}
                        >
                          No HR contacts mapped yet.
                        </div>
                      )
                    ) : (
                      <ExecList>
                        {hrList.map((c, i) => {
                          const initials = (c.name || "?")
                            .split(/\s+/)
                            .map((s) => s[0])
                            .join("")
                            .toUpperCase()
                            .slice(0, 2);
                          const tints = [
                            ...AVATAR_TINTS,
                          ];
                          const tint = tints[i % tints.length];
                          const hasEmail = !!(c.probable_email || c.email);
                          return (
                            <ExecRow key={i}>
                              <ExecAvatar
                                $tintBg={tint.bg}
                                $tintFg={tint.fg}
                              >
                                {initials}
                              </ExecAvatar>
                              <div style={{ minWidth: 0 }}>
                                <ExecName>{c.name || "Unknown"}</ExecName>
                                <ExecTitle>
                                  {c.position || "—"}
                                  {c.location ? ` · ${c.location}` : ""}
                                </ExecTitle>
                              </div>
                              {hasEmail ? (
                                <ButtonGhost
                                  style={{ height: 30, padding: "0 10px", fontSize: 12 }}
                                  onClick={() => openOutreachForPerson(c)}
                                >
                                  Email
                                </ButtonGhost>
                              ) : (
                                <PillTag style={{ opacity: 0.6 }}>
                                  No email
                                </PillTag>
                              )}
                            </ExecRow>
                          );
                        })}
                      </ExecList>
                    )}
                  </FirmCard>

                  {/* Latest job postings */}
                  <FirmCard $span={7} data-tour="research-jobs">
                    <FirmCardHead>
                      <div>
                        <FirmCardH>Latest job postings</FirmCardH>
                        <FirmCardSub>
                          {recentJobs.length} most recent ·{" "}
                          {formatNumber(
                            hiringTrends?.total ?? jobsList.length,
                          )}{" "}
                          total
                        </FirmCardSub>
                      </div>
                      {(hiringTrends?.total ?? jobsList.length) > 0 && (
                        <ButtonGhost
                          style={{
                            height: 30,
                            padding: "0 12px",
                            fontSize: 12.5,
                          }}
                          onClick={() =>
                            navigate(
                              `/jobs?tab=all&source=${encodeURIComponent(companyName)}`,
                            )
                          }
                        >
                          View all
                          <FiChevronRight size={12} />
                        </ButtonGhost>
                      )}
                    </FirmCardHead>
                    {recentJobs.length === 0 ? (
                      <div
                        style={{
                          padding: "30px 0",
                          textAlign: "center",
                          color: t.inkMuted,
                          fontSize: 13,
                        }}
                      >
                        No jobs available.
                      </div>
                    ) : (
                      <JobList>
                        {recentJobs.map((j, i) => {
                          const id = j._id?.$oid || j._id;
                          return (
                            <JobRow
                              key={id || i}
                              onClick={() => {
                                // Open in the Jobs page full-page detail
                                // view (same template as /jobs). The
                                // `fromExternal` flag tells Jobs to pop
                                // back here on the Back button rather than
                                // just closing the inline detail.
                                if (id) {
                                  try {
                                    sessionStorage.setItem(
                                      "research-scroll-y",
                                      String(window.scrollY || 0),
                                    );
                                  } catch (_) {}
                                  navigate(
                                    `/jobs?jobId=${encodeURIComponent(id)}`,
                                    {
                                      state: {
                                        fromExternal: true,
                                        fromLabel: "research",
                                      },
                                    },
                                  );
                                }
                              }}
                            >
                              {(() => {
                                // Extract a clean list of location strings from
                                // whatever shape the backend returned. Long jobs
                                // can carry 20+ cities in modified_location.
                                const extractList = (val) => {
                                  if (Array.isArray(val))
                                    return val
                                      .map((x) =>
                                        typeof x === "string" ? x.trim() : "",
                                      )
                                      .filter(Boolean);
                                  if (typeof val === "string" && val.trim())
                                    return [val.trim()];
                                  return [];
                                };
                                let list = [];
                                const loc = j.location;
                                if (loc && typeof loc === "object" && !Array.isArray(loc)) {
                                  list =
                                    extractList(loc.modified_location) ||
                                    [];
                                  if (list.length === 0)
                                    list = extractList(loc.original_location);
                                  if (list.length === 0)
                                    list = extractList(loc.modified_country);
                                  if (list.length === 0)
                                    list = extractList(loc.original_country);
                                } else {
                                  list = extractList(loc);
                                }
                                if (list.length === 0)
                                  list = extractList(j.country);
                                const firstLoc = list[0] || "";
                                const extra = Math.max(0, list.length - 1);
                                return (
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <JobRowTitle className="job-row-title">
                                      {j.title ||
                                        j.job_title ||
                                        j.position ||
                                        "Untitled role"}
                                    </JobRowTitle>
                                    <JobRowMeta>
                                      {firstLoc && (
                                        <span
                                          style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 3,
                                          }}
                                        >
                                          <FiMapPin size={12} />
                                          {firstLoc}
                                          {extra > 0 && (
                                            <span
                                              style={{
                                                color: t.inkMuted,
                                                marginLeft: 4,
                                              }}
                                            >
                                              +{extra} other location
                                              {extra > 1 ? "s" : ""}
                                            </span>
                                          )}
                                        </span>
                                      )}
                                  {j.posted_date && (
                                    <>
                                      <span className="sep">·</span>
                                      <span>
                                        {relativeTime(j.posted_date)}
                                      </span>
                                    </>
                                  )}
                                  {j.category && (
                                    <>
                                      <span className="sep">·</span>
                                      <span>{j.category}</span>
                                    </>
                                  )}
                                    </JobRowMeta>
                                  </div>
                                );
                              })()}
                              <IconButton
                                aria-label={
                                  bookmarks.includes(id)
                                    ? "Remove bookmark"
                                    : "Bookmark job"
                                }
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleBookmark(j);
                                }}
                                style={{
                                  background: bookmarks.includes(id)
                                    ? t.accentSoft
                                    : "transparent",
                                  border: "1px solid transparent",
                                  color: bookmarks.includes(id)
                                    ? t.accent
                                    : t.inkMuted,
                                }}
                              >
                                <FiBookmark size={12} />
                              </IconButton>
                            </JobRow>
                          );
                        })}
                      </JobList>
                    )}
                  </FirmCard>

                  {/* Signals / news */}
                  <FirmCard $span={5} data-tour="research-signals">
                    <FirmCardHead>
                      <div>
                        <FirmCardH>Latest signals</FirmCardH>
                        <FirmCardSub>
                          News, hiring &amp; changes
                          {scrapedNewsTotal > 0
                            ? ` · ${formatNumber(scrapedNewsTotal)} tracked`
                            : ""}
                        </FirmCardSub>
                      </div>
                      <AiPill>
                        <Spark size={12} /> AI-tracked
                      </AiPill>
                    </FirmCardHead>
                    {newsList.length === 0 ? (
                      <div
                        style={{
                          padding: "30px 0",
                          textAlign: "center",
                          color: t.inkMuted,
                          fontSize: 13,
                        }}
                      >
                        No recent signals.
                      </div>
                    ) : (
                      <SignalList>
                        {newsList.map((n, i) => (
                          <SignalRow key={i}>
                            <SignalIcon
                              $bg={t.accentSoft}
                              $fg={t.accentInk}
                            >
                              <Spark size={12} />
                            </SignalIcon>
                            <div style={{ minWidth: 0 }}>
                              <SignalText>
                                <a
                                  href={n.original_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: "inherit",
                                    textDecoration: "none",
                                  }}
                                >
                                  {n.title}
                                </a>
                              </SignalText>
                              <SignalWhen>
                                {n.source || ""}
                                {n.published
                                  ? ` · ${relativeTime(n.published)}`
                                  : ""}
                              </SignalWhen>
                            </div>
                          </SignalRow>
                        ))}
                      </SignalList>
                    )}
                  </FirmCard>
                </FirmGrid>
                  </FadeIn>
                )}
              </>
            );
          })()}


        {/* Show all regions modal/popup */}
        {/* Show all tiers modal/popup */}
        {showAllTiers &&
          (() => {
            // Check if it's from result (company page) or companyData (company cards list)
            const tiers =
              result && result.tiers
                ? result.tiers
                : companyData[showAllTiers]
                  ? companyData[showAllTiers].tiers
                  : {};
            const companyName = result ? result.title || query : showAllTiers;

            if (!tiers || Object.keys(tiers).length === 0) return null;

            const toggleTier = (tier) => {
              setCollapsedTiers((prev) => {
                const next = new Set(prev);
                if (next.has(tier)) {
                  next.delete(tier);
                } else {
                  next.add(tier);
                }
                return next;
              });
            };

            // Tier color mapping - gradient from dark grey to dark blue (dark blue = last/highest tier number)
            const tierColors = TIER_RAMP;

            // 1-based tiers into a 0-based ramp; anything unknown lands on the top tier.
            const getTierColor = (tier) =>
              tierColors[Number(tier) - 1] || tierColors[tierColors.length - 1];

            return createPortal(
              <ModalOverlay>
                <PageScrollLock />
                <ModalContent
                  style={{ maxWidth: "900px", maxHeight: "85vh" }}
                >
                  <ModalClose onClick={() => setShowAllTiers(null)}>
                    &times;
                  </ModalClose>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      marginBottom: 24,
                    }}
                  >
                    <img
                      src="/assets/icon.webp"
                      alt="Legal500"
                      style={{
                        width: "32px",
                        height: "24px",
                        objectFit: "contain",
                      }}
                    />
                    <h3
                      style={{
                        margin: 0,
                        color: TOKENS.accent,
                        fontSize: "1.5rem",
                      }}
                    >
                      Category Rankings
                    </h3>
                  </div>
                  <div
                    style={{
                      color: "var(--ink-muted)",
                      marginBottom: 24,
                      fontSize: "0.95rem",
                    }}
                  >
                    {companyName}
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gap: 20,
                      maxHeight: "calc(85vh - 180px)",
                      overflowY: "auto",
                      paddingRight: "8px",
                    }}
                  >
                    {Object.entries(tiers)
                      .sort(([a], [b]) => parseInt(a) - parseInt(b))
                      .map(([tier, categories]) => {
                        const colors = getTierColor(tier);
                        const isCollapsed = collapsedTiers.has(tier);

                        return (
                          <div
                            key={tier}
                            style={{
                              background: colors.bg,
                              border: `2px solid ${colors.border}`,
                              borderRadius: "16px",
                              padding: "20px",
                              position: "relative",
                              overflow: "hidden",
                            }}
                          >
                            {/* Tier Badge with Collapse Button */}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: isCollapsed ? 0 : 16,
                                cursor: "pointer",
                              }}
                              onClick={() => toggleTier(tier)}
                            >
                              <div
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 8,
                                  padding: "6px 14px",
                                  background: colors.badge,
                                  color: "var(--on-fill)",
                                  borderRadius: "20px",
                                  fontSize: "0.85rem",
                                  fontWeight: 700,
                                  boxShadow: `0 2px 8px ${colors.badge}40`,
                                }}
                              >
                                <span>Tier {tier}</span>
                                <span
                                  style={{
                                    background: "color-mix(in srgb, var(--on-fill) 30%, transparent)",
                                    padding: "2px 8px",
                                    borderRadius: "12px",
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  {Array.isArray(categories)
                                    ? categories.length
                                    : 0}
                                </span>
                              </div>
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                style={{
                                  transform: isCollapsed
                                    ? "rotate(-90deg)"
                                    : "rotate(0deg)",
                                  transition: "transform 0.2s ease",
                                  color: "var(--ink)",
                                  cursor: "pointer",
                                }}
                              >
                                <polyline points="6 9 12 15 18 9"></polyline>
                              </svg>
                            </div>

                            {/* Categories Grid - Collapsible */}
                            {!isCollapsed && (
                              <div
                                style={{
                                  display: "grid",
                                  // min() so a 280px track can't overflow the tier card inside a phone-width modal.
                                  gridTemplateColumns:
                                    "repeat(auto-fill, minmax(min(280px, 100%), 1fr))",
                                  gap: "10px",
                                  marginTop: 12,
                                  animation: "fadeIn 0.2s ease",
                                }}
                              >
                                {Array.isArray(categories) &&
                                  categories.map((category, idx) => {
                                    // Parse category: country (left after first comma), category (left after last comma), tiertext (right after last comma)
                                    const parts = category
                                      .split(",")
                                      .map((p) => p.trim());
                                    const country = parts[0] || "";
                                    const categoryName =
                                      parts.length > 2
                                        ? parts.slice(1, -1).join(", ")
                                        : parts[1] || "";
                                    const tierText =
                                      parts.length > 1
                                        ? parts[parts.length - 1]
                                        : "";

                                    return (
                                      <div
                                        key={idx}
                                        style={{
                                          background: "var(--surface)",
                                          border: `1px solid ${"var(--line)"}`,
                                          borderRadius: "10px",
                                          padding: "12px 14px",
                                          transition: "all 0.2s ease",
                                          cursor: "default",
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.transform =
                                            "translateY(-2px)";
                                          e.currentTarget.style.boxShadow =
                                            "var(--shadow-md)";
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.transform =
                                            "translateY(0)";
                                          e.currentTarget.style.boxShadow =
                                            "none";
                                        }}
                                      >
                                        {country && (
                                          <div
                                            style={{
                                              fontSize: "0.75rem",
                                              color: "var(--ink-muted)",
                                              marginBottom: "4px",
                                              fontWeight: 500,
                                            }}
                                          >
                                            {country}
                                          </div>
                                        )}
                                        {categoryName && (
                                          <div
                                            style={{
                                              fontSize: "0.9rem",
                                              color: "var(--ink)",
                                              fontWeight: 600,
                                              marginBottom: tierText
                                                ? "4px"
                                                : "0",
                                            }}
                                          >
                                            {categoryName}
                                          </div>
                                        )}
                                        {tierText && (
                                          <div
                                            style={{
                                              fontSize: "0.8rem",
                                              color: "var(--ink-muted)",
                                              fontStyle: "italic",
                                              marginTop: "4px",
                                            }}
                                          >
                                            {tierText}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </ModalContent>
              </ModalOverlay>,
              document.body,
            );
          })()}
      </Container>
    </>
  );
};

export default ResearchPage;
